import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { 
  Save, Grid, Type, List, Copy, ArrowLeft, ArrowRight, Move,
  ChevronLeft, ChevronRight, Layout, Maximize2,
  Check, Lock, Unlock,
  ChevronUp, ChevronDown, X, Box, PanelLeftClose, PanelLeftOpen,
  Hash, Rows, Columns, Code, Timer
} from 'lucide-react';
import api from '../../services/api';
import userProfileService from '../../services/userProfileService';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import versionHistoryService from '../../services/versionHistoryService';
import FirstQuestionModal from './FirstQuestionModal';
import ConfirmDialog from '../common/ConfirmDialog';
import UnifiedItemBank from './UnifiedItemBank';
import DeviceModeModal from './ScreenBuilder/DeviceModeModal';
import PublishModal from './ScreenBuilder/PublishModal';
import RuleEditorModal from './ScreenBuilder/RuleEditorModal';
import BulkAddModal from './ScreenBuilder/BulkAddModal';
import AlertModal from './ScreenBuilder/AlertModal';
import PublishedWarningModal from './ScreenBuilder/PublishedWarningModal';
import PreviewMode from './ScreenBuilder/PreviewMode';
import ScreenTabs from './ScreenBuilder/ScreenTabs';
import ZoomControls from './ScreenBuilder/ZoomControls';
import VersionHistorySidebar from './ScreenBuilder/VersionHistorySidebar';
import SidebarPanel from './ScreenBuilder/SidebarPanel';
import BuilderToolbar from './ScreenBuilder/BuilderToolbar';
import ChangesRequestedBanner from './ChangesRequestedBanner';
import ManageAssessment from './ManageAssessment';
import CanvasArea from './ScreenBuilder/CanvasArea';
import usePreviewMode from './ScreenBuilder/hooks/usePreviewMode';
import useElementOperations from './ScreenBuilder/hooks/useElementOperations';
import useScreenOperations from './ScreenBuilder/hooks/useScreenOperations';
import useSaveOperations from './ScreenBuilder/hooks/useSaveOperations';
import useAssessmentBuilderUndoRedo from './ScreenBuilder/hooks/useAssessmentBuilderUndoRedo';
import useAssessmentLock from './ScreenBuilder/hooks/useAssessmentLock';
import { transformScreensForDeviceChange } from './ScreenBuilder/deviceModeUtils';
import { buildCanvasOptionsFromUpdatedItem, buildResponseConfigForType, deriveBooleanOptionsIfNeeded, computeGroupDragPositions, computeGroupNudgePositions, toggleSelection } from './ScreenBuilder/utils';
import { DEFAULT_GLOBAL_FORMATTING } from './ScreenBuilder/constants';

import toast from 'react-hot-toast';

// Review states where an assessment has left draft and must not be edited in
// place. Opening one prompts to fork a new draft version instead, preserving
// the completed copy (its pending/signed review and any study references).
const LOCKED_EDIT_STATUSES = ['in_review', 'approved', 'published'];

const ScreenBuilder = ({ assessmentConfig, onBack, onSave, onAssessmentChange }) => {
  const { user } = useContext(AuthContext);
  const [screens, setScreensBase] = useState([]);
  const [currentScreenIndex, setCurrentScreenIndexBase] = useState(0);
  const [items, setItems] = useState([]);
  const [localAssessmentConfig, setLocalAssessmentConfig] = useState(assessmentConfig);
  const [selectedElement, setSelectedElement] = useState(null);
  // Multi-select (FECOA-1523): the set of selected element ids. `selectedElement`
  // remains the *anchor* (last-clicked; drives the properties panel + resize
  // handles) and is always a member of this set when non-null. Single-select
  // code paths that only call `setSelectedElement` stay correct via a sync
  // effect below that collapses the set to the anchor on a fresh selection.
  const [selectedElements, setSelectedElements] = useState(() => new Set());
  const groupDragOffsetsRef = useRef(null); // per-member {dx,dy} during a group drag
  const clipboardRef = useRef([]); // in-memory copy/paste buffer for canvas elements
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState(null);

  // Load preferences from localStorage
  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem('assessmentBuilderPreferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
    return null;
  };

  const savedPrefs = loadPreferences();

  // UI state variables with saved preferences
  const [deviceMode, setDeviceMode] = useState(savedPrefs?.deviceMode || 'desktop-standard');
  const [showGrid, setShowGrid] = useState(savedPrefs?.showGrid ?? true);
  const [snapToGrid, setSnapToGrid] = useState(savedPrefs?.snapToGrid ?? true);
  const [gridSize, setGridSize] = useState(savedPrefs?.gridSize || 10);
  const [showProperties, setShowProperties] = useState(savedPrefs?.showProperties ?? true);
  const [canvasZoom, setCanvasZoom] = useState(savedPrefs?.canvasZoom || 1);
  const [showAlignmentGuides, setShowAlignmentGuides] = useState(savedPrefs?.showAlignmentGuides ?? true);
  const [showPositionOverlay, setShowPositionOverlay] = useState(savedPrefs?.showPositionOverlay ?? true);
  const [showZoomPresets, setShowZoomPresets] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activePanel, setActivePanel] = useState('elements');
  const [sidebarPosition, setSidebarPosition] = useState(savedPrefs?.sidebarPosition || 'right');
  const [sidebarWidth, setSidebarWidth] = useState(384);
  const [isResizing, setIsResizing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [manageModalAssessment, setManageModalAssessment] = useState(null);
  const [editingAssessmentName, setEditingAssessmentName] = useState(false);
  const [assessmentNameValue, setAssessmentNameValue] = useState('');
  const [showDeviceModeModal, setShowDeviceModeModal] = useState(false);
  const [showItemBank, setShowItemBank] = useState(false);
  const [editItemFromSidebar, setEditItemFromSidebar] = useState(null); // Item to auto-edit when opening item bank
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [pendingDeviceMode, setPendingDeviceMode] = useState(null);
  const [selectedScalingOption, setSelectedScalingOption] = useState('proportional');
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggingFromSidebar, setDraggingFromSidebar] = useState(null);
  const [dragPreviewPosition, setDragPreviewPosition] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });
  const [showPublishedWarningModal, setShowPublishedWarningModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishName, setPublishName] = useState('');
  const [creatingNewVersion, setCreatingNewVersion] = useState(false);
  const [showVersionsDropdown, setShowVersionsDropdown] = useState(false);
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [showPreviewDropdown, setShowPreviewDropdown] = useState(false);

  // Button Layout Template states
  const [buttonTemplates, setButtonTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [selectedButtonTemplate, setSelectedButtonTemplate] = useState('');
  const [setTemplateAsDefault, setSetTemplateAsDefault] = useState(false);
  const [templateSaveLevel, setTemplateSaveLevel] = useState('user'); // 'user' or 'assessment'
  
  // Question Layout Template cache - preloaded for instant modal opening
  const [allTemplatesCache, setAllTemplatesCache] = useState({ system: [], sponsor: [], user: [], assessment: [], appliedDefaultIds: [] });
  const [templatesCacheLoaded, setTemplatesCacheLoaded] = useState(false);

  // Skip Logic state
  const [showRuleEditorModal, setShowRuleEditorModal] = useState(false);
  // TEMPORARILY DISABLED: Skip Logic Templates
  // The template feature allows users to save and reuse skip logic patterns.
  // However, skip logic is highly assessment-specific (screen IDs, question IDs, thresholds)
  // making reusable templates less practical than originally planned.
  // To re-enable: uncomment this state, the loadNavigationTemplates/applyNavigationTemplateToQuestion
  // functions, the useEffect for showTemplatePickerModal, the "Use Template" button, and the
  // TemplatePickerModal component. Backend endpoints exist but need CREATE/UPDATE/DELETE endpoints
  // added before users can save their own templates.
  // const [showTemplatePickerModal, setShowTemplatePickerModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [navigationRules, setNavigationRulesBase] = useState({});
  const [ruleFormData, setRuleFormData] = useState({
    condition_type: '',
    operator: '>=',
    value: '',
    question_ids: [],
    action_type: '',
    target_screen_id: '',
    alert_message: '',
    enabled: true
  });
  // TEMPORARILY DISABLED: Template state - see note above
  // const [navigationTemplates, setNavigationTemplates] = useState({ system_templates: [], sponsor_templates: [] });
  // const [selectedTemplate, setSelectedTemplate] = useState(null);
  // const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // First Question Modal state
  const [showFirstQuestionModal, setShowFirstQuestionModal] = useState(false);
  const [firstQuestionType, setFirstQuestionType] = useState(null);
  const [pendingElement, setPendingElement] = useState(null);
  const [autoShowLayoutModal, setAutoShowLayoutModal] = useState(() => {
    return localStorage.getItem('autoShowLayoutModal') !== 'false'; // Default: true
  });
  
  // Question layout template state
  const [questionTemplateName, setQuestionTemplateName] = useState('');
  const [questionTemplateAsDefault, setQuestionTemplateAsDefault] = useState(false);
  
  // Bulk screen creation state
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkScreenCount, setBulkScreenCount] = useState(5);

  // Device fit validation warnings (JIRA-699)
  const [deviceFitWarnings, setDeviceFitWarnings] = useState([]);

  const {
    setScreens,
    setCurrentScreenIndex,
    setNavigationRules,
    undo: undoHistory,
    redo: redoHistory,
    canUndo,
    canRedo,
    runWithoutUndo,
    beginInteractionBatch,
    endInteractionBatch,
    clearHistory,
  } = useAssessmentBuilderUndoRedo({
    screens,
    currentScreenIndex,
    navigationRules,
    setScreensBase,
    setCurrentScreenIndexBase,
    setNavigationRulesBase,
  });

  // Refs for performance optimization
  const rafIdRef = useRef(null);
  const rafIdResizeRef = useRef(null);
  const deviceModeInitializedFromMetadata = useRef(false);
  const versionHistoryRef = useRef(null);

  // Device dimensions - Specific device models for clinical validation
  const deviceDimensions = {
    // Mobile Devices
    'iphone-se': { width: 375, height: 667, name: 'iPhone SE/8 (375x667)', category: 'phone' },
    'iphone-12': { width: 390, height: 844, name: 'iPhone 12/13 (390x844)', category: 'phone' },
    'iphone-14-pro': { width: 393, height: 852, name: 'iPhone 14 Pro (393x852)', category: 'phone' },
    'samsung-s21': { width: 360, height: 800, name: 'Samsung Galaxy S21 (360x800)', category: 'phone' },
    'pixel-5': { width: 393, height: 851, name: 'Google Pixel 5 (393x851)', category: 'phone' },
    
    // Tablets
    'ipad-10': { width: 810, height: 1080, name: 'iPad 10.2" (810x1080)', category: 'tablet' },
    'ipad-air': { width: 820, height: 1180, name: 'iPad Air (820x1180)', category: 'tablet' },
    'ipad-pro-11': { width: 834, height: 1194, name: 'iPad Pro 11" (834x1194)', category: 'tablet' },
    'ipad-pro-13': { width: 1024, height: 1366, name: 'iPad Pro 12.9" (1024x1366)', category: 'tablet' },
    'galaxy-tab-s8': { width: 800, height: 1280, name: 'Galaxy Tab S8 (800x1280)', category: 'tablet' },
    'surface-go': { width: 800, height: 1280, name: 'Surface Go (800x1280)', category: 'tablet' },
    
    // Desktop/Laptop
    'laptop-13': { width: 1280, height: 800, name: 'MacBook Air 13" (1280x800)', category: 'desktop' },
    'laptop-14': { width: 1440, height: 900, name: 'MacBook Pro 14" (1440x900)', category: 'desktop' },
    'laptop-15': { width: 1366, height: 768, name: 'Common Laptop 15" (1366x768)', category: 'desktop' },
    'desktop-hd': { width: 1920, height: 1080, name: 'Desktop HD (1920x1080)', category: 'desktop' },
    'desktop-standard': { width: 1200, height: 800, name: 'Standard Desktop (1200x800)', category: 'desktop' },
    
    // Clinical Device Presets
    'clinical-tablet': { width: 768, height: 1024, name: 'Clinical Tablet Standard (768x1024)', category: 'tablet' },
    'clinical-laptop': { width: 1280, height: 900, name: 'Clinical Laptop Standard (1280x900)', category: 'desktop' },

    // Responsive (fluid) canvas — fills the full width of whatever screen it is
    // displayed on. width/height are the nominal design-space dimensions; the
    // rendered width is always 100% of the container. Mirrors DEVICE_DIMENSIONS
    // in constants.js (kept in sync). `responsive: true` is the discriminator.
    'responsive': { width: 1200, height: 800, name: 'Responsive (Full width)', category: 'responsive', responsive: true }
  };

  // FECOA-827: Filter device dimensions based on study's allowed_device_types
  // If the study has restrictions, only show matching device categories in the selector.
  // Empty allowed_device_types means no restrictions (show all).
  const studyAllowedTypes = localAssessmentConfig?.studies?.[0]?.allowed_device_types || [];
  const filteredDeviceDimensions = useMemo(() => {
    if (!studyAllowedTypes.length) return deviceDimensions;
    return Object.fromEntries(
      Object.entries(deviceDimensions).filter(([_, device]) =>
        // The responsive (fluid) canvas is device-agnostic — it adapts to any
        // screen — so it stays available regardless of the study's allowed
        // device categories (FECOA-1532).
        device.category === 'responsive' || studyAllowedTypes.includes(device.category)
      )
    );
  }, [studyAllowedTypes.join(',')]);

  // If current device mode isn't in the filtered set, switch to the first available
  useEffect(() => {
    if (studyAllowedTypes.length && !filteredDeviceDimensions[deviceMode]) {
      const firstAvailable = Object.keys(filteredDeviceDimensions)[0];
      if (firstAvailable) {
        setDeviceMode(firstAvailable);
      }
    }
  }, [filteredDeviceDimensions, deviceMode]);

  // Handle device mode change request - show confirmation if elements exist
  const handleDeviceModeChange = (newMode) => {
    const currentScreen = screens[currentScreenIndex];

    // Transitions to/from the responsive (fluid) canvas never rescale elements:
    // the responsive canvas has no fixed target width, so proportional/center
    // scaling against its nominal dimensions would be misleading. Keep element
    // positions exactly as-is and skip the scaling prompt (FECOA-1532).
    const involvesResponsive = newMode === 'responsive' || deviceMode === 'responsive';

    // Check if current screen has elements
    if (!involvesResponsive && currentScreen && currentScreen.elements && currentScreen.elements.length > 0) {
      // Show confirmation modal
      setPendingDeviceMode(newMode);
      setSelectedScalingOption('proportional'); // Default to recommended option
      setShowDeviceModeModal(true);
    } else {
      // No elements (or a responsive transition) — safe to switch directly.
      applyDeviceModeChange(newMode, 'none');
    }
  };

  // Apply device mode change with scaling option
  const applyDeviceModeChange = (newMode, scalingOption) => {
    const oldDims = deviceDimensions[deviceMode];
    const newDims = deviceDimensions[newMode];
    
    const scaleX = newDims.width / oldDims.width;
    const scaleY = newDims.height / oldDims.height;
    
    setDeviceMode(newMode);

    if (screens.length > 0) {
      const updatedScreens = transformScreensForDeviceChange(screens, oldDims, newDims, newMode, scalingOption);
      setScreens(updatedScreens);
    }
    
    setShowDeviceModeModal(false);
    setPendingDeviceMode(null);
  };

  // Add fullscreen class to body when component mounts
  useEffect(() => {
    document.body.classList.add('screenbuilder-active');
    return () => {
      document.body.classList.remove('screenbuilder-active');
    };
  }, []);

  // Sync local assessment config when prop changes
  useEffect(() => {
    if (assessmentConfig) {
      console.debug('📋 ScreenBuilder - assessmentConfig updated:', assessmentConfig);
      console.debug('📋 survey_id:', assessmentConfig.survey_id);
      setLocalAssessmentConfig(assessmentConfig);
    }
  }, [assessmentConfig]);

  // Open the Manage Assessment modal on the Settings tab — fetches the full
  // assessment detail (with survey_detail.adaptive_config) so the Adaptive
  // section has current engine values to display.
  const openSettingsModal = useCallback(async () => {
    const uuid = localAssessmentConfig?.assessment_uuid || assessmentConfig?.assessment_uuid;
    if (!uuid) {
      console.warn('[ScreenBuilder] Cannot open settings: missing assessment_uuid');
      return;
    }
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${uuid}/`);
      setManageModalAssessment(response.data);
    } catch (error) {
      console.error('[ScreenBuilder] Failed to load assessment for settings modal:', error);
    }
  }, [localAssessmentConfig, assessmentConfig]);

  // Preload all templates on mount for instant modal opening
  useEffect(() => {
    const preloadAllTemplates = async () => {
      if (!assessmentConfig?.assessment_id || templatesCacheLoaded) return;
      
      try {
        const questionTypes = ['ordinal', 'rating', 'nrs', 'vas', 'text', 'yes_no', 'nominal', 'dropdown', 'date', 'numeric', 'boolean', 'checkbox', 'multiple_choice', 'instruction'];
        const allTemplatesMap = { system: [], sponsor: [], user: [], assessment: [] };
        const appliedDefaultIds = new Set();

        for (const qType of questionTypes) {
          try {
            const response = await api.get('/api/assessment-builder/layout-templates/available/', {
              params: {
                question_type: qType,
                assessment_id: assessmentConfig.assessment_uuid
              }
            });

            if (response.data.system_templates) {
              allTemplatesMap.system.push(...response.data.system_templates);
            }
            if (response.data.sponsor_templates) {
              allTemplatesMap.sponsor.push(...response.data.sponsor_templates);
            }
            if (response.data.user_templates) {
              allTemplatesMap.user.push(...response.data.user_templates);
            }
            if (response.data.assessment_templates) {
              allTemplatesMap.assessment.push(...response.data.assessment_templates);
            }
            if (response.data.assessment_templates_from_other_assessments) {
              allTemplatesMap.assessment.push(...response.data.assessment_templates_from_other_assessments.map(a => a.template));
            }
            if (response.data.applied_default_template_id) {
              appliedDefaultIds.add(response.data.applied_default_template_id);
            }
          } catch (err) {
            console.error(`Failed to preload templates for ${qType}:`, err);
          }
        }

        // Remove duplicates by ID
        const uniqueSystem = Array.from(new Map(allTemplatesMap.system.map(t => [t.id, t])).values());
        const uniqueSponsor = Array.from(new Map(allTemplatesMap.sponsor.map(t => [t.id, t])).values());
        const uniqueUser = Array.from(new Map(allTemplatesMap.user.map(t => [t.id, t])).values());
        const uniqueAssessment = Array.from(new Map(allTemplatesMap.assessment.map(t => [t.id, t])).values());

        setAllTemplatesCache({
          system: uniqueSystem,
          sponsor: uniqueSponsor,
          user: uniqueUser,
          assessment: uniqueAssessment,
          appliedDefaultIds: [...appliedDefaultIds]
        });
        setTemplatesCacheLoaded(true);
        console.debug('✓ Templates preloaded:', uniqueSystem.length + uniqueSponsor.length + uniqueUser.length + uniqueAssessment.length, 'total');
      } catch (error) {
        console.error('Failed to preload templates:', error);
      }
    };
    
    preloadAllTemplates();
  }, [assessmentConfig?.assessment_id, templatesCacheLoaded]);

  // Allow child components to trigger a cache refresh (e.g. after "Use for Assessment")
  const refreshTemplatesCache = () => setTemplatesCacheLoaded(false);

  // Derive applied default template IDs from localAssessmentConfig (always up-to-date)
  const appliedDefaultTemplateIds = useMemo(() => {
    const questionTemplates = localAssessmentConfig?.default_templates?.config?.question_templates || {};
    return Object.values(questionTemplates).map(t => t.template_id).filter(Boolean);
  }, [localAssessmentConfig?.default_templates]);

  // Form-wide formatting (font family, base font size, line spacing) — FECOA-732.
  // Falls back to the publisher defaults when the assessment has no override yet.
  const globalFormatting = useMemo(() => ({
    ...DEFAULT_GLOBAL_FORMATTING,
    ...(localAssessmentConfig?.default_templates?.config?.global_formatting || {})
  }), [localAssessmentConfig?.default_templates]);

  // Persist a global formatting change (or reset) and mirror it into localAssessmentConfig
  // so the cascade into new elements picks it up immediately. Returns the saved value.
  const handleGlobalFormattingChange = async (payload) => {
    const assessmentUuid = localAssessmentConfig?.assessment_uuid || assessmentConfig?.assessment_uuid;
    if (!assessmentUuid) {
      toast.error('Cannot save formatting: assessment not loaded');
      return null;
    }
    try {
      const result = await AssessmentBuilderAPI.setGlobalFormatting(assessmentUuid, payload);
      const saved = result?.global_formatting || { ...DEFAULT_GLOBAL_FORMATTING };
      setLocalAssessmentConfig(prev => ({
        ...prev,
        default_templates: {
          ...(prev.default_templates || {}),
          config: {
            ...(prev.default_templates?.config || {}),
            global_formatting: saved
          }
        }
      }));

      // Cascade the form font/size/spacing onto existing on-canvas text elements
      // (questions, instructions, free text). These are global controls, so every
      // unlocked element re-styles to match; locked elements keep their own styling.
      // Question text and response options both read these from element.style, so
      // updating element.style is enough.
      let appliedCount = 0;
      let lockedSkipCount = 0;
      const updatedScreens = screens.map(screen => ({
        ...screen,
        elements: (screen.elements || []).map(el => {
          if (!['question', 'instruction', 'text'].includes(el.type)) return el;
          if (el.isLocked) {
            lockedSkipCount += 1;
            return el;
          }
          appliedCount += 1;
          return {
            ...el,
            style: {
              ...(el.style || {}),
              fontFamily: saved.fontFamily,
              fontSize: saved.baseFontSize,
              lineHeight: saved.lineSpacing,
            },
          };
        }),
      }));
      if (appliedCount > 0) {
        setScreens(updatedScreens);
      }

      const lockedSuffix = lockedSkipCount > 0 ? `, ${lockedSkipCount} locked skipped` : '';
      const base = payload?.reset ? 'Reset to publisher defaults' : 'Form formatting saved';
      toast.success(appliedCount > 0 ? `${base} · applied to ${appliedCount} element${appliedCount > 1 ? 's' : ''}${lockedSuffix}` : base);
      return saved;
    } catch (error) {
      console.error('Failed to save global formatting:', error);
      toast.error(error?.response?.data?.error || 'Failed to save form formatting');
      return null;
    }
  };

  // Superuser read-only bypass: lets a superuser open a published assessment
  // without forking a draft. The bypass can be entered two ways:
  //   1) From the dashboard modal (sets `_superuserBypass` on the config prop).
  //   2) From the in-builder PublishedWarningModal (after a hard refresh,
  //      we land directly in the builder without going through the dashboard).
  // `superuserBypassActive` controls banner visibility; `superuserReadOnly`
  // controls whether saves are gated. The ref keeps the latest read-only state
  // visible to useSaveOperations without re-running the hook initializer.
  const [superuserBypassActive, setSuperuserBypassActive] = useState(
    assessmentConfig?._superuserBypass === true
  );
  const [superuserReadOnly, setSuperuserReadOnly] = useState(
    assessmentConfig?._superuserBypass === true
  );
  // Approver review mode: a hard, non-toggleable read-only view (no Save, no
  // edit chrome). Distinct from the superuser bypass, which can unlock to edit.
  const reviewReadOnly = assessmentConfig?._reviewReadOnly === true;
  // View mode (FECOA-1700, AC-5): same hard read-only as review, but entered
  // from the dashboard's View button on a published assessment — no review
  // action bar below and no "Pending Review" badge (the Published badge
  // already communicates the lock).
  const viewOnly = assessmentConfig?._viewOnly === true;

  // Exclusive edit lock (FECOA-1302): only draft assessments are lockable, and
  // we never contend a lock while a superuser is read-only-bypassing or an
  // approver is reviewing (those modes already gate saves).
  const lockEnabled = (
    (assessmentConfig?.publication_status || 'draft') === 'draft'
    && !reviewReadOnly
    && !superuserBypassActive
  );
  const {
    isReadOnly: lockReadOnly,
    holder: lockHolder,
    since: lockSince,
    reason: lockReason,
  } = useAssessmentLock(assessmentConfig?.assessment_uuid, { enabled: lockEnabled });

  const isReadOnlyRef = useRef(superuserReadOnly || reviewReadOnly || lockReadOnly);
  useEffect(() => {
    isReadOnlyRef.current = superuserReadOnly || reviewReadOnly || lockReadOnly;
  }, [superuserReadOnly, reviewReadOnly, lockReadOnly]);

  // Hide/disable the editing chrome (toolbar edit controls, sidebars, canvas
  // interaction) whenever the assessment is read-only because of the approver
  // review flow OR because another user holds the edit lock (FECOA-1302, AC-2).
  // The superuser bypass is intentionally excluded — it keeps the chrome so the
  // superuser can toggle back to editing. Save is separately blocked via
  // isReadOnlyRef for all three.
  const editChromeReadOnly = reviewReadOnly || lockReadOnly;

  // Show warning modal when opening a locked assessment (in_review/approved/
  // published, or an active survey). Skip when the superuser bypass is active
  // or an approver is in read-only review mode.
  useEffect(() => {
    if (superuserBypassActive || reviewReadOnly) return;
    if (LOCKED_EDIT_STATUSES.includes(assessmentConfig?.publication_status) || assessmentConfig?.status === 'active') {
      setShowPublishedWarningModal(true);
    }
  }, [assessmentConfig?.assessment_id, superuserBypassActive, reviewReadOnly]);

  const enterSuperuserReadOnlyBypass = () => {
    setSuperuserBypassActive(true);
    setSuperuserReadOnly(true);
    setShowPublishedWarningModal(false);
  };

  // Fetch assessment data and items
  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!assessmentConfig?.assessment_id) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/api/assessment-builder/assessments/${assessmentConfig.assessment_uuid}/`);
        const data = response.data;
        
        console.debug('[LOAD] Assessment data received:', data);
        
        // Merge builder_metadata from detailed payload into local config so
        // downstream effects (like device metadata initialization) can use it.
        if (data.builder_metadata) {
          setLocalAssessmentConfig(prev => ({
            ...prev,
            builder_metadata: {
              ...(prev?.builder_metadata || {}),
              ...data.builder_metadata,
            },
          }));
        }
        
        // Extract questions/items from the assessment
        // Backend returns 'items' field with CDISC metadata
        const questionList = data.items || data.questions || [];
        setItems(questionList);

        // Screens are nested in survey_detail
        const screensData = data.survey_detail?.screens || data.screens || [];
        console.debug('[LOAD] Screens data:', screensData);

        clearHistory();
        runWithoutUndo(() => {
          // Initialize with one empty screen if no screens exist
          if (!screensData || screensData.length === 0) {
            console.log('[LOAD] No screens found, creating default screen');
            setScreens([createNewScreen('Screen 1', 0)]);
          } else {
            console.debug('[LOAD] Loading', screensData.length, 'screens');
            
            // DEDUPLICATE: Remove duplicate screens based on ID, then name+rank
            const deduplicatedScreens = [];
            const seenIds = new Set();
            const seenNameRanks = new Set();
            
            screensData.forEach(screen => {
              const nameRankKey = `${screen.name}_${screen.rank}`;
              
              // Keep screen if ID is unique AND name+rank combo is unique
              if (!seenIds.has(screen.id) && !seenNameRanks.has(nameRankKey)) {
                seenIds.add(screen.id);
                seenNameRanks.add(nameRankKey);
                deduplicatedScreens.push(screen);
              } else {
                console.debug('[LOAD] Duplicate screen detected and removed:', screen.name, screen.rank);
              }
            });
            
            // Rerank screens and mark ALL question elements as library items (read-only)
            // Build a lookup from question ID to current item data for hydration
            const itemLookup = {};
            for (const item of questionList) {
              itemLookup[item.id] = item;
            }

            const rerankedScreens = deduplicatedScreens.map((screen, idx) => ({
              ...screen,
              rank: idx,
              // ALL question/instruction elements should be read-only - they come from the item library
              elements: (screen.elements || []).map(el => {
                if (el.type === 'question' || el.type === 'instruction') {
                  // Hydrate from the live Question record so item-bank edits
                  // propagate to elements placed on existing screens. Without
                  // this, the canvas keeps showing whatever was snapshotted
                  // into layout_data at last save.
                  const freshItem = itemLookup[el.questionId];
                  if (!freshItem) {
                    // FECOA-1643: even without a live item to hydrate from, a
                    // yes/no element persisted with the raw {type:'boolean',
                    // yesLabel, noLabel} dict (or no options) must be normalized
                    // to a real options array or the canvas renders nothing.
                    // Derive from the element's own stored options dict.
                    const derived = deriveBooleanOptionsIfNeeded(el, el.data?.options);
                    if (derived) {
                      return {
                        ...el,
                        isLibraryItem: true,
                        data: { ...el.data, options: derived },
                        responseOptions: derived,
                      };
                    }
                    return { ...el, isLibraryItem: true };
                  }
                  // FECOA-1481: instructions don't have response types/options;
                  // only refresh text + code so the canvas shows the latest copy.
                  if (el.type === 'instruction') {
                    const nextData = { ...el.data };
                    if (typeof freshItem.text === 'string' && nextData.text !== freshItem.text) {
                      nextData.text = freshItem.text;
                    }
                    if (freshItem.code && nextData.code !== freshItem.code) {
                      nextData.code = freshItem.code;
                    }
                    return {
                      ...el,
                      isLibraryItem: true,
                      isModified: !!freshItem.is_modified,
                      libraryLevel: freshItem.library_level,
                      ...(typeof freshItem.text === 'string' && el.content !== freshItem.text
                        ? { content: freshItem.text }
                        : {}),
                      data: nextData,
                    };
                  }
                  const nextData = { ...el.data };
                  const typeChanged = freshItem.type && nextData.type !== freshItem.type;
                  if (typeChanged) {
                    nextData.type = freshItem.type;
                  }
                  if (typeof freshItem.text === 'string' && nextData.text !== freshItem.text) {
                    nextData.text = freshItem.text;
                  }
                  // FECOA-1437: if the item's response type changed in the bank,
                  // the previously-saved responseConfig is stale — rebuild it
                  // so the canvas widget swaps to the new type's controls.
                  const rebuilt = typeChanged
                    ? buildResponseConfigForType(freshItem.type, freshItem.options)
                    : null;
                  if (rebuilt) {
                    nextData.options = rebuilt.responseOptions.length > 0
                      ? rebuilt.responseOptions
                      : freshItem.options;
                  }
                  // FECOA-1643: when the type didn't change (so `rebuilt` is
                  // null and responseConfig/styles are preserved) but the yes/no
                  // element was persisted with the raw options dict — or with no
                  // options — derive the proper 2-option array. This repairs
                  // already-saved assessments so the canvas shows the buttons and
                  // the preview shows the item's real labels, WITHOUT disturbing
                  // the user's chosen display/alignment/width (responseConfig).
                  const derivedBool = !typeChanged
                    ? deriveBooleanOptionsIfNeeded({ ...el, data: nextData }, freshItem.options)
                    : null;
                  if (derivedBool) {
                    nextData.options = derivedBool;
                  }
                  return {
                    ...el,
                    isLibraryItem: true,
                    isModified: !!freshItem.is_modified,
                    libraryLevel: freshItem.library_level,
                    // Keep `content` in lockstep with data.text — both are read
                    // by canvas/navigation panels and updateElementContent
                    // already maintains this invariant for inline edits.
                    ...(typeof freshItem.text === 'string' && el.content !== freshItem.text
                      ? { content: freshItem.text }
                      : {}),
                    ...(rebuilt
                      ? { responseConfig: rebuilt.responseConfig, responseOptions: rebuilt.responseOptions }
                      : {}),
                    ...(derivedBool ? { responseOptions: derivedBool } : {}),
                    data: nextData,
                  };
                }
                return el;
              })
            }));
            
            console.debug('[LOAD] After deduplication:', rerankedScreens.length, 'screens (removed', screensData.length - rerankedScreens.length, 'duplicates)');
            setScreens(rerankedScreens);
          }
        });

      } catch (error) {
        console.error('Error fetching assessment data:', error);
        clearHistory();
        runWithoutUndo(() => {
          setScreens([createNewScreen('Screen 1', 0)]);
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentData();
  }, [assessmentConfig, setScreens, refreshCounter, clearHistory, runWithoutUndo]);

  // One-time initialization of deviceMode from backend device metadata
  useEffect(() => {
    if (deviceModeInitializedFromMetadata.current) {
      return;
    }

    const deviceContext = localAssessmentConfig?.builder_metadata?.device_context;

    if (!deviceContext) {
      return;
    }

    if (!screens || screens.length === 0) {
      return;
    }

    let targetMode = null;
    const type = deviceContext.device_type;

    if (type === 'phone') {
      targetMode = 'iphone-12';
    } else if (type === 'tablet') {
      targetMode = 'clinical-tablet';
    } else if (type === 'desktop') {
      targetMode = 'desktop-standard';
    }

    if (!targetMode || targetMode === deviceMode) {
      return;
    }

    const newDims = deviceDimensions[targetMode];
    const oldDims = deviceDimensions[deviceMode];
    if (!newDims || !oldDims) {
      return;
    }

    // Programmatic fit from study metadata — not a user edit; must not consume an undo step
    runWithoutUndo(() => {
      applyDeviceModeChange(targetMode, 'none');
    });
    deviceModeInitializedFromMetadata.current = true;
  }, [assessmentConfig, screens, deviceMode, runWithoutUndo]);

  // Save auto-show modal preference
  useEffect(() => {
    localStorage.setItem('autoShowLayoutModal', String(autoShowLayoutModal));
  }, [autoShowLayoutModal]);

  // Load preferences from backend on mount (localStorage is already applied synchronously above)
  const backendPrefsLoaded = useRef(false);
  useEffect(() => {
    if (backendPrefsLoaded.current) return;
    backendPrefsLoaded.current = true;
    userProfileService.getUIPreferences().then(serverPrefs => {
      const prefs = serverPrefs?.assessmentBuilder;
      if (prefs) {
        // Apply backend prefs and update localStorage cache
        if (prefs.deviceMode) setDeviceMode(prefs.deviceMode);
        if (prefs.showGrid !== undefined) setShowGrid(prefs.showGrid);
        if (prefs.snapToGrid !== undefined) setSnapToGrid(prefs.snapToGrid);
        if (prefs.gridSize) setGridSize(prefs.gridSize);
        if (prefs.showProperties !== undefined) setShowProperties(prefs.showProperties);
        if (prefs.canvasZoom) setCanvasZoom(prefs.canvasZoom);
        if (prefs.showAlignmentGuides !== undefined) setShowAlignmentGuides(prefs.showAlignmentGuides);
        if (prefs.showPositionOverlay !== undefined) setShowPositionOverlay(prefs.showPositionOverlay);
        if (prefs.sidebarPosition) setSidebarPosition(prefs.sidebarPosition);
        try {
          localStorage.setItem('assessmentBuilderPreferences', JSON.stringify(prefs));
        } catch (e) { /* ignore */ }
      }
    });
  }, []);

  // Save preferences to localStorage immediately + backend (debounced)
  const prefsSaveTimeout = useRef(null);
  useEffect(() => {
    const preferences = {
      deviceMode,
      showGrid,
      snapToGrid,
      gridSize,
      showProperties,
      canvasZoom,
      showAlignmentGuides,
      showPositionOverlay,
      sidebarPosition
    };

    // Immediate localStorage write
    try {
      localStorage.setItem('assessmentBuilderPreferences', JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }

    // Debounced backend write (2s)
    if (prefsSaveTimeout.current) clearTimeout(prefsSaveTimeout.current);
    prefsSaveTimeout.current = setTimeout(() => {
      userProfileService.updateUIPreferences({ assessmentBuilder: preferences }).catch(() => {});
    }, 2000);

    return () => { if (prefsSaveTimeout.current) clearTimeout(prefsSaveTimeout.current); };
  }, [deviceMode, showGrid, snapToGrid, gridSize, showProperties, canvasZoom, showAlignmentGuides, showPositionOverlay, sidebarPosition]);

  // Sync loaded screens with selected device mode on mount
  useEffect(() => {
    if (screens.length > 0 && deviceMode) {
      const targetDims = deviceDimensions[deviceMode];
      const currentDims = screens[0]?.layout;
      
      // Only sync if dimensions don't match
      if (currentDims && (currentDims.width !== targetDims.width || currentDims.height !== targetDims.height)) {
        console.log(`[SYNC] Canvas mismatch detected. Syncing to ${deviceMode}:`, targetDims);
        
        const updatedScreens = screens.map(screen => ({
          ...screen,
          layout: {
            ...screen.layout,
            width: targetDims.width,
            height: targetDims.height,
            deviceMode: deviceMode
          }
        }));
        
        runWithoutUndo(() => {
          setScreens(updatedScreens);
        });
      }
    }
  }, [screens.length, deviceMode, runWithoutUndo, setScreens]);

  // Snap position to grid
  const snapPosition = useCallback((value) => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  }, [snapToGrid, gridSize]);

  // Ref populated below once updateElementsWithScreenQuestionIds is defined.
  // The hook reads .current at save-completion time, so the wiring resolves
  // despite the function being declared later in the component body.
  const updateElementsWithScreenQuestionIdsRef = useRef(null);

  // Save operations hook (must be before useElementOperations which needs setSaveStatus)
  const {
    saveStatus,
    setSaveStatus,
    lastSaved,
    setLastSaved,
    autoSaveEnabled,
    setAutoSaveEnabled,
    saving,
    setSaving,
    handleSave,
    saveRestoredVersion,
    setIsUpdatingScreenQuestionIds,
  } = useSaveOperations({
    screens,
    setScreens,
    items,
    currentScreenIndex,
    setCurrentScreenIndex,
    deviceMode,
    canvasZoom,
    assessmentConfig,
    onSave,
    updateElementsWithScreenQuestionIdsRef,
    versionHistoryRef,
    changeSummary,
    runWithoutUndo,
    isReadOnlyRef,
  });

  // Element operations hook
  const {
    lockedElements,
    setLockedElements,
    elementGroups,
    setElementGroups,
    showOutOfBoundsWarning,
    setShowOutOfBoundsWarning,
    updateElementPosition,
    updateElementPositions,
    updateElementSize,
    updateElementContent,
    updateElementStyle,
    updateResponseConfig,
    alignElement,
    deleteElement,
    toggleLockElement,
    isElementLocked,
    ungroupQuestion,
    regroupQuestion,
    updateSubElementPosition,
    updateSubElementSize,
    autoArrangeOptions,
    distributeOptions,
    alignOptions,
    getOutOfBoundsElements,
    fitElementsToCanvas,
    getElementGroup,
    ungroupElement,
  } = useElementOperations({
    screens,
    setScreens,
    currentScreenIndex,
    selectedElement,
    setSelectedElement,
    snapPosition,
    setConfirmDialog,
    setSaveStatus,
  });

  // --- Multi-select helpers (FECOA-1523) ---------------------------------
  // Fresh single selection: anchor + set collapse to just this id (or clear).
  const selectSingleElement = useCallback((id) => {
    setSelectedElement(id);
    setSelectedElements(id == null ? new Set() : new Set([id]));
  }, []);

  // Additive Shift/Ctrl+click: toggle membership, reassign the anchor. Reads the
  // set from render closure (handlers are recreated each render) so we never
  // schedule a setState from inside another setState's updater.
  const toggleElementInSelection = (id) => {
    const { next, anchor } = toggleSelection(selectedElements, id);
    setSelectedElements(next);
    setSelectedElement(anchor);
  };

  // Delete the current (possibly multi-) selection behind a confirm dialog.
  // Locked members are kept. Shared by the Delete/Backspace key handler and the
  // multi-select panel's "Delete selected" button (FECOA-1523).
  const deleteSelectedElements = () => {
    if (selectedElements.size === 0) return;
    const currentScreen = screens[currentScreenIndex];
    const ids = [...selectedElements];
    const deletable = ids.filter((id) => !lockedElements.has(id));
    if (deletable.length === 0) {
      toast.warning(
        ids.length > 1
          ? 'All selected elements are locked. Unlock them first.'
          : 'Cannot delete a locked element. Unlock it first.'
      );
      return;
    }

    const screenLabel = currentScreen?.name || currentScreen?.id || `Screen ${currentScreenIndex + 1}`;
    let title;
    let message;
    if (deletable.length === 1) {
      const element = currentScreen.elements.find((el) => el.id === deletable[0]);
      const elementType = element?.type || 'element';
      const elementDescription = element?.data?.text || element?.label || element?.content || element?.code || '';
      const descriptionText = elementDescription ? `\n\n"${elementDescription}"` : '';
      title = `Delete ${elementType}?`;
      message = `Are you sure you want to delete this ${elementType} from screen "${screenLabel}"?${descriptionText}`;
    } else {
      title = `Delete ${deletable.length} items?`;
      const lockedCount = ids.length - deletable.length;
      const lockedNote = lockedCount > 0
        ? ` (${lockedCount} locked item${lockedCount !== 1 ? 's' : ''} will be kept)`
        : '';
      message = `Are you sure you want to delete ${deletable.length} items from screen "${screenLabel}"?${lockedNote}`;
    }

    const deleteSet = new Set(deletable);
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      type: 'danger',
      onConfirm: () => {
        setScreens(screens.map((screen, index) =>
          index === currentScreenIndex
            ? { ...screen, elements: screen.elements.filter((el) => !deleteSet.has(el.id)) }
            : screen
        ));
        setSelectedElement(null); // sync effect clears the selection set
      },
    });
  };

  // Screen operations hook
  const {
    draggingScreenIndex,
    setDraggingScreenIndex,
    dragOverScreenIndex,
    setDragOverScreenIndex,
    editingScreenId,
    setEditingScreenId,
    editingScreenName,
    setEditingScreenName,
    createNewScreen,
    addNewScreen,
    addBulkScreens,
    deleteScreen,
    deleteAllScreens,
    duplicateScreen,
    handleScreenDragStart,
    handleScreenDragOver,
    handleScreenDrop,
    handleScreenDragEnd,
    startEditingScreenName,
    saveScreenName,
    cancelEditingScreenName,
  } = useScreenOperations({
    screens,
    setScreens,
    currentScreenIndex,
    setCurrentScreenIndex,
    deviceMode,
    deviceDimensions,
    setConfirmDialog,
    setShowBulkAddModal,
    setActivePanel,
  });

  // Calculate dynamic question height based on response options
  const calculateQuestionHeight = (item, responseConfig = {}, elementPadding = 16) => {
    const baseHeight = 80; // Question text + margin
    const optionHeight = (responseConfig.optionFontSize || 14) + (responseConfig.optionPadding || 8) * 2 + 4;
    const spacing = responseConfig.spacing || 8;
    const opts = item.options || item.response_options || item.responseOptions || [];
    const numOptions = Array.isArray(opts) ? opts.length : 0;
    const layout = responseConfig.layout || 'vertical';
    // Account for element padding (top + bottom) since overflow is hidden
    const pad = elementPadding * 2;

    // Handle type-specific layouts first
    const itemType = (item.type || item.question_type || item.response_type || '').toLowerCase();
    if (itemType === 'body_map') {
      // Body map = two 600px-tall figures + side panel; account for label + padding.
      return baseHeight + 640 + pad;
    }

    if (layout === 'vas') {
      return baseHeight + 120 + pad;
    } else if (layout === 'nrs') {
      const orientation = responseConfig.orientation || 'horizontal';
      if (orientation === 'vertical') {
        const numButtons = (responseConfig.maxValue || 10) - (responseConfig.minValue || 0) + 1;
        return baseHeight + (numButtons * 44) + 40 + pad;
      }
      return baseHeight + 100 + pad;
    } else if (layout === 'thermometer') {
      // ThermometerScale renders vertically at ~300px height by default;
      // the grouped render clamps via `Math.min(element.size.height - 60, 400)`,
      // so size the element to give the scale its full 300px + baseline.
      return baseHeight + 340 + pad;
    } else if (layout === 'text') {
      return baseHeight + (responseConfig.multiline ? 120 : 60) + pad;
    } else if (layout === 'boolean') {
      return baseHeight + 80 + pad;
    }

    // For standard layouts, check if there are options
    if (numOptions === 0) return baseHeight + 40 + pad;

    if (layout === 'vertical') {
      return baseHeight + (optionHeight * numOptions) + (spacing * (numOptions - 1)) + 20 + pad;
    } else if (layout === 'horizontal') {
      const estimatedRows = Math.ceil(numOptions / 3);
      return baseHeight + (optionHeight * estimatedRows) + (spacing * (estimatedRows - 1)) + 20 + pad;
    } else if (layout === 'grid') {
      const columns = responseConfig.columns || 2;
      const rows = Math.ceil(numOptions / columns);
      return baseHeight + (optionHeight * rows) + (spacing * (rows - 1)) + 20 + pad;
    }

    return baseHeight + 100 + pad; // Default fallback
  };

  /**
   * Load navigation rules from backend when a question is selected
   * @param {number} screenQuestionId - The backend screen_question_id for API call
   * @param {string} elementId - The frontend element ID to use as storage key
   */
  const loadNavigationRulesForQuestion = async (screenQuestionId, elementId, screensForEnrichment = null) => {
    try {
      const response = await api.get(`/api/assessment-builder/screen-questions/${screenQuestionId}/navigation-rules/`);
      const data = response.data;
      // Pass the full response object to converter (it expects { skip_rules: [...] })
      const localRules = AssessmentBuilderAPI.convertApiRulesToLocalFormat(data);
      
      // Enrich rules with target_screen_index and target_screen_name for reliable matching
      // The backend only stores target_screen_id, so we need to resolve index/name on load
      const currentScreens = screensForEnrichment || screens;
      const enrichedRules = localRules.map(rule => {
        if (rule.action_type === 'skip_to' && rule.target_screen_id) {
          // Try multiple strategies to find the target screen
          let targetIdx = -1;
          let matchMethod = 'none';
          
          // 1. Try by frontend screen.id
          targetIdx = currentScreens.findIndex(s => String(s.id) === String(rule.target_screen_id));
          if (targetIdx !== -1) matchMethod = 'id';
          
          // 2. Try by backend survey_screen_id (screen IDs from database)
          if (targetIdx === -1) {
            targetIdx = currentScreens.findIndex(s => String(s.survey_screen_id) === String(rule.target_screen_id));
            if (targetIdx !== -1) matchMethod = 'survey_screen_id';
          }
          
          // 3. Try by screen name if we have it stored
          if (targetIdx === -1 && rule.target_screen_name) {
            targetIdx = currentScreens.findIndex(s => s.name === rule.target_screen_name);
            if (targetIdx !== -1) matchMethod = 'name';
          }
          
          const targetScreen = targetIdx !== -1 ? currentScreens[targetIdx] : null;
          
          console.log('[Rule Enrich] Resolving target for rule:', {
            target_screen_id: rule.target_screen_id,
            foundIdx: targetIdx,
            matchMethod,
            foundName: targetScreen?.name
          });
          
          return {
            ...rule,
            target_screen_index: targetIdx !== -1 ? targetIdx : rule.target_screen_index,
            target_screen_name: targetScreen?.name || rule.target_screen_name
          };
        }
        return rule;
      });
      
      // Store under elementId for consistent lookup in UI and preview mode
      const storageKey = elementId || screenQuestionId;
      setNavigationRules(prev => ({
        ...prev,
        [storageKey]: enrichedRules
      }));
      
      return localRules;
    } catch (error) {
      // 404 = stale screen_question_id, silently initialize empty rules
      if (error.response?.status !== 404) {
        console.error('Error loading navigation rules:', error);
      }
      const storageKey = elementId || screenQuestionId;
      setNavigationRules(prev => ({ ...prev, [storageKey]: [] }));
      return [];
    }
  };

  // Preview mode hook (must be after loadNavigationRulesForQuestion is defined)
  const {
    previewMode,
    previewScreenIndex,
    previewResponses,
    setPreviewResponses,
    previewStartTime,
    previewScreenTimes,
    previewNavigationLog,
    setPreviewNavigationLog,
    previewCompleted,
    previewShowDebug,
    setPreviewShowDebug,
    previewValidationErrors,
    previewSkipLogMessage,
    previewNavigationHistory,
    previewScreenVisits,
    previewFiredRuleIds,
    setPreviewFiredRuleIds,
    previewVisitedQuestionIds,
    previewSkippedQuestions,
    setPreviewSkippedQuestions,
    enterPreviewMode,
    exitPreviewMode,
    navigatePreviewScreen,
    navigatePreviewBack,
    completePreviewAssessment,
    validateCurrentScreen,
    loadAllNavigationRulesForPreview
  } = usePreviewMode({
    screens,
    navigationRules,
    loadNavigationRulesForQuestion,
    setSelectedElement
  });

  /**
   * Save navigation rules to backend
   */
  const saveNavigationRulesToBackend = async (screenQuestionId, rules) => {
    // Validate screen_question_id before attempting to save
    if (!screenQuestionId || screenQuestionId === 'undefined' || screenQuestionId === 'null') {
      console.warn('[Nav Rules] Cannot save - no valid screen_question_id. Save the assessment first.');
      toast.warning('Please save the assessment first to enable rule persistence');
      return null;
    }
    
    try {
      const apiRules = AssessmentBuilderAPI.convertSkipRulesToApiFormat(rules);
      
      const payload = {
        skip_rules: apiRules,
        is_required: false, // Get from element data
        validation_message: 'This question is required'
      };
      
      const response = await api.put(`/api/assessment-builder/screen-questions/${screenQuestionId}/navigation-rules/`, payload);
      
      console.log('Navigation rules saved:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error saving navigation rules:', error);
      // Check if this is a foreign key / not found error (stale screen_question_id)
      const errorMsg = error.response?.data?.error?.toLowerCase() || '';
      const errorDetail = error.response?.data?.detail?.toLowerCase() || '';
      const statusCode = error.response?.status;
      
      if (statusCode === 404 || errorMsg.includes('notfound') || errorMsg.includes('not found') || 
          errorDetail.includes('does not exist') || errorDetail.includes('no screenquestion') ||
          errorMsg.includes('foreign key') || errorMsg.includes('bad request')) {
        console.warn('[Nav Rules] screen_question_id may be stale. Rules saved locally, will sync on next assessment save.');
        toast.warning('Rule saved locally. It will sync when you save the assessment.');
        return null; // Don't throw - rule is saved locally
      }
      toast.error(`Error saving rules: ${error.response?.data?.error || error.response?.data?.detail || 'Unknown error'}`);
      throw error;
    }
  };

  /**
   * Update elements with screen_question_ids from backend mapping
   */
  const updateElementsWithScreenQuestionIds = async (mapping) => {
    if (!mapping || Object.keys(mapping).length === 0) return;
    console.log('[SQ Mapping] Updating elements with screen_question_ids:', mapping);
    
    // Prevent auto-save loop - this update shouldn't trigger another save
    setIsUpdatingScreenQuestionIds(true);
    
    // Update screens state (server sync — not an end-user edit)
    runWithoutUndo(() => {
      setScreens(prevScreens => 
        prevScreens.map(screen => ({
          ...screen,
          elements: screen.elements?.map(element => {
            if (mapping[element.id]) {
              return { ...element, screen_question_id: mapping[element.id] };
            }
            return element;
          })
        }))
      );
    });

    // Save any pending navigation rules that now have screen_question_ids
    for (const [elementId, screenQuestionId] of Object.entries(mapping)) {
      const rules = navigationRules[elementId];
      if (rules && rules.length > 0) {
        console.log(`[SQ Mapping] Saving pending rules for element ${elementId} -> sq ${screenQuestionId}`);
        try {
          await saveNavigationRulesToBackend(screenQuestionId, rules);
        } catch (err) {
          console.error(`Failed to save rules for ${elementId}:`, err);
        }
      }
    }
    
    // Reset the flag after a short delay to allow React state update to complete
    setTimeout(() => {
      setIsUpdatingScreenQuestionIds(false);
    }, 100);
  };

  // Wire the ref so useSaveOperations can invoke the latest version after each render.
  updateElementsWithScreenQuestionIdsRef.current = updateElementsWithScreenQuestionIds;

  // TEMPORARILY DISABLED: Skip Logic Templates - see state declaration for details
  // /**
  //  * Load navigation templates from backend
  //  */
  // const loadNavigationTemplates = async () => {
  //   try {
  //     const response = await api.get('/api/assessment-builder/navigation-templates/');
  //     const data = response.data;
  //     return data;
  //   } catch (error) {
  //     console.error('Error loading navigation templates:', error);
  //     return { system_templates: [], sponsor_templates: [] };
  //   }
  // };

  // /**
  //  * Apply a navigation template to current question
  //  */
  // const applyNavigationTemplateToQuestion = async (screenQuestionId, templateId, parameters) => {
  //   try {
  //     const response = await api.post(`/api/assessment-builder/screen-questions/${screenQuestionId}/apply-template/`, {
  //       template_id: templateId,
  //       parameters: parameters
  //     });
  //     
  //     // Reload rules after applying template
  //     await loadNavigationRulesForQuestion(screenQuestionId);
  //     
  //     setAlertModal({
  //       show: true,
  //       title: 'Template Applied',
  //       message: response.data.message,
  //       type: 'success'
  //     });
  //     
  //     return response.data;
  //   } catch (error) {
  //     console.error('Error applying navigation template:', error);
  //     setAlertModal({
  //       show: true,
  //       title: 'Error Applying Template',
  //       message: error.response?.data?.error,
  //       type: 'error'
  //     });
  //     throw error;
  //   }
  // };

  /**
   * Handle First Question Modal selection
   */
  const handleFirstQuestionTemplateSelected = async (selection) => {
    console.log('[TEMPLATE] handleFirstQuestionTemplateSelected called with:', selection);
    console.debug('[TEMPLATE] pendingElement:', pendingElement);
    
    if (!pendingElement) {
      console.error('[TEMPLATE] No pending element!');
      return;
    }

    const { template, setAsDefault } = selection;
    console.log('[TEMPLATE] Template selected:', template?.name, 'setAsDefault:', setAsDefault);

    // Save template as default if requested
    if (setAsDefault && template && assessmentConfig?.assessment_id) {
      console.log('[TEMPLATE] ✓ Saving template as default...');
      
      try {
        const response = await AssessmentBuilderAPI.setLayoutDefault({
          template_id: template.id,
          assessment_id: assessmentConfig.assessment_uuid,
          question_type: normalizeQuestionType(pendingElement.item.question_type || pendingElement.item.type),
          set_as_default: true
        });
        console.log('[TEMPLATE] ✓ API call succeeded:', response.data);
        
        // Update assessmentConfig with the new default template
        const questionType = normalizeQuestionType(pendingElement.item.question_type || pendingElement.item.type);
        console.log('[TEMPLATE] Question type for storage:', questionType);
        
        setLocalAssessmentConfig(prev => {
          console.debug('[TEMPLATE] Previous question_templates:', prev.default_templates?.config?.question_templates);
          
          const updatedConfig = {
            ...prev,
            default_templates: {
              ...(prev.default_templates || {}),
              config: {
                ...(prev.default_templates?.config || {}),
                question_templates: {
                  ...(prev.default_templates?.config?.question_templates || {}),
                  [questionType]: {
                    template_id: template.id,
                    template_name: template.name,
                    question_type: questionType,
                    layout_config: template.layout_config
                  }
                }
              }
            }
          };
          
          console.log('[TEMPLATE] ✓ Updated question_templates:', updatedConfig.default_templates.config.question_templates);
          return updatedConfig;
        });
        
        console.log('[TEMPLATE] ✓ State updated with default template for:', questionType);
      } catch (error) {
        console.error('[TEMPLATE] ❌ Error setting layout default:', error);
        console.error('[TEMPLATE] Error response:', error.response?.data);
      }
    } else {
      console.log('[TEMPLATE] ✗ NOT saving as default:', {
        setAsDefault,
        hasTemplate: !!template,
        hasAssessmentId: !!assessmentConfig?.assessment_id
      });
    }

    // Extract template layout config if template was selected
    const templateLayoutConfig = template?.layout_config || null;
    console.debug('[TEMPLATE] Using template config:', templateLayoutConfig);
    
    // Add the element to the screen with template config
    addElementToScreen(
      pendingElement.itemType,
      pendingElement.item,
      pendingElement.customPosition,
      true, // skipFirstQuestionCheck
      templateLayoutConfig // Apply template layout
    );

    console.log('[TEMPLATE] ✓ Element added, closing modal');
    
    // Clear pending state and close modal
    setPendingElement(null);
    setFirstQuestionType(null);
    setShowFirstQuestionModal(false);
  };

  const startEditingAssessmentName = () => {
    setAssessmentNameValue(localAssessmentConfig?.name || 'Assessment Builder');
    setEditingAssessmentName(true);
  };

  const saveAssessmentName = async () => {
    if (assessmentNameValue.trim()) {
      try {
        const response = await api.patch(`/api/assessment-builder/assessments/${assessmentConfig.assessment_uuid}/`, { name: assessmentNameValue.trim() });
        const data = response.data;
        
        console.log('Assessment name updated successfully');
        
        // Create rename snapshot
        if (assessmentConfig?.survey_id && screens.length > 0) {
          try {
            const configuration = {
              screens,
              deviceMode,
              metadata: {
                element_count: screens.reduce((sum, screen) => sum + (screen.elements?.length || 0), 0),
                screen_count: screens.length,
              }
            };

            await versionHistoryService.renameSnapshot(
              assessmentConfig.survey_id,
              configuration,
              localAssessmentConfig.name,
              assessmentNameValue.trim(),
              {
                device_mode: deviceMode,
                canvas_zoom: canvasZoom,
              }
            );

            console.log('Rename snapshot created successfully');
          } catch (snapshotError) {
            console.error('Failed to create rename snapshot:', snapshotError);
            // Don't fail the rename if snapshot fails
          }
        }
        
        // Update local config state
        const updatedConfig = {
          ...localAssessmentConfig,
          name: assessmentNameValue.trim()
        };
        setLocalAssessmentConfig(updatedConfig);
        
        // Notify parent to update its state so it doesn't overwrite with stale name
        if (onAssessmentChange) {
          onAssessmentChange(updatedConfig);
        }
      } catch (error) {
        console.error('Error saving assessment name:', error);
      }
    }
    setEditingAssessmentName(false);
  };

  const cancelEditingAssessmentName = () => {
    setEditingAssessmentName(false);
    setAssessmentNameValue('');
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const response = await api.post(`/api/assessment-builder/assessments/${assessmentConfig.assessment_uuid}/publish/`, {
        change_summary: changeSummary,
        name: publishName.trim() || undefined
      });
      const data = response.data;
      const schedulesUpdated = data.schedules_updated || 0;
      const studiesUpdated = data.studies_updated || 0;
      const detail =
        schedulesUpdated > 0
          ? ` — swapped ${schedulesUpdated} schedule${schedulesUpdated === 1 ? '' : 's'} to the new version`
          : studiesUpdated > 0
            ? ` — ${studiesUpdated} stud${studiesUpdated === 1 ? 'y' : 'ies'} moved to the new version`
            : '';
      toast.success(`Successfully published as v${data.version}${detail}`);
      setShowPublishModal(false);
      setChangeSummary('');
      setPublishName('');
      // Navigate back to Assessment Builder dashboard after publishing
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('Error publishing:', error);
      const validationErrors = error.response?.data?.validation_errors;
      
      if (validationErrors && validationErrors.length > 0) {
        // Show detailed validation errors in alert modal
        setAlertModal({
          show: true,
          title: 'Cannot Publish Assessment',
          message: (
            <div>
              <p className="mb-3">Please fix the following issues before publishing:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {validationErrors.map((err, idx) => (
                  <li key={idx} className="text-red-600">{err}</li>
                ))}
              </ul>
            </div>
          ),
          type: 'error'
        });
      } else {
        const errorMsg = error.response?.data?.error || 'Failed to publish assessment';
        toast.error(errorMsg);
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleCreateNewVersion = async () => {
    setCreatingNewVersion(true);
    try {
      const response = await api.post(`/api/assessment-builder/assessments/${assessmentConfig.assessment_uuid}/create-new-version/`, {});
      const data = response.data;
      toast.success(`Created new draft version ${data.version}`);

      // Update assessment config in parent without page reload
      if (onAssessmentChange) {
        const newConfig = {
          assessment_id: data.new_assessment_id,
          assessment_uuid: data.new_assessment_uuid,
          survey_id: data.new_survey_id,
          name: data.name,
          library_type: assessmentConfig.library_type,
          assessment_type: assessmentConfig.assessment_type,
          publication_status: 'draft',
          version: data.version,
          studies: assessmentConfig.studies || [],
          mode: 'edit'
        };
        console.log('[CREATE NEW VERSION] Switching to new assessment:', newConfig);
        onAssessmentChange(newConfig);
      } else {
        // Fallback to page navigation if callback not provided
        window.location.href = `/assessment-builder/${data.new_assessment_uuid}`;
      }
    } catch (error) {
      console.error('Error creating new version:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create new version';
      toast.error(errorMsg);
    } finally {
      setCreatingNewVersion(false);
    }
  };

  const fetchVersionHistory = async () => {
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentConfig.assessment_uuid}/version-history/`);
      setVersionHistory(response.data);
    } catch (error) {
      console.error('Error fetching version history:', error);
    }
  };

  const removeAssessmentItem = (item) => {
    if (!item) return;
    
    // Remove from items list
    setItems(prevItems => prevItems.filter(existingItem => {
      if (existingItem.id === item.id) return false;
      if (existingItem.code && item.code && existingItem.code === item.code) return false;
      return true;
    }));
    
    // Also remove any elements on screens that reference this item
    setScreens(prevScreens => prevScreens.map(screen => ({
      ...screen,
      elements: screen.elements.filter(el => {
        if (el.type !== 'question') return true;
        return el.questionId !== item.id && el.itemData?.id !== item.id;
      })
    })));
    
    toast.success(`Removed "${item.code || item.text?.substring(0, 30) || 'item'}" from assessment`);
  };

  const removeAllItems = () => {
    if (!items.length) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Remove All Items?',
      message: `This will remove all ${items.length} items from the assessment sidebar and any placed on screens. This cannot be undone.`,
      type: 'danger',
      confirmText: 'Remove All',
      onConfirm: () => {
        setItems([]);
        // Clear all question elements from screens, keep buttons/text
        setScreens(prevScreens => prevScreens.map(screen => ({
          ...screen,
          elements: screen.elements.filter(el => el.type !== 'question' && el.type !== 'instruction')
        })));
        setConfirmDialog({ isOpen: false });
        toast.success(`Removed ${items.length} items`);
      }
    });
  };

  // Maps raw response_type values (from CDISC/item bank) to the question_type keys
  // used by the template system. Keeps lookups consistent regardless of item source.
  const normalizeQuestionType = (rawType) => {
    const map = {
      likert:   'ordinal',
      numeric:  'rating',
      // vas is its own type in the model, rating templates cover both NRS+VAS
      // everything else passes through unchanged
    };
    return map[rawType] || rawType || 'ordinal';
  };

  const addElementToScreen = (itemType, item = null, customPosition = null, skipFirstQuestionCheck = false, templateConfig = null) => {
    const currentScreen = screens[currentScreenIndex];
    
    // Prevent adding duplicate questions to ANY screen (not just current)
    if (itemType === 'question' && item?.id) {
      // Check ALL screens for this item
      for (let screenIdx = 0; screenIdx < screens.length; screenIdx++) {
        const screen = screens[screenIdx];
        const questionAlreadyOnScreen = screen.elements.some(
          el => el.type === 'question' && (el.questionId === item.id || el.itemData?.id === item.id)
        );
        if (questionAlreadyOnScreen) {
          const screenName = screen.name || `Screen ${screenIdx + 1}`;
          console.warn(`[SCREEN BUILDER] Question ${item.id} already exists on ${screenName}, skipping duplicate`);
          toast.error(`This item is already placed on "${screenName}". Each item can only appear once in the assessment.`);
          return; // Don't add duplicate
        }
      }
    }

    // Enforce one assessment item per screen (FECOA-1463).
    // An "item" is a question OR an instruction — both arrive from the items panel as
    // itemType 'question'. Buttons / dividers are layout elements and remain unrestricted.
    if (itemType === 'question' && item) {
      const itemsOnScreen = currentScreen.elements.filter(
        el => el.type === 'question' || el.type === 'instruction'
      ).length;
      if (itemsOnScreen >= 1) {
        const screenName = currentScreen.name || `Screen ${currentScreenIndex + 1}`;
        console.warn(`[SCREEN BUILDER] ${screenName} already has an item, blocking second add`);
        // FECOA-1492: message describes the real rule (one item per screen), not just questions.
        toast.error(`"${screenName}" already contains an item. Each screen can hold only one item — add a new screen to place another.`);
        return;
      }
    }
    
    // Check if this is the first question of this type (for First Question Modal)
    // OR if user has enabled auto-show layout modal preference
    // Skip this check if called from the modal handler itself
    if (!skipFirstQuestionCheck && itemType === 'question' && item && assessmentConfig?.assessment_id) {
      const questionType = normalizeQuestionType(item.question_type || item.type);
      
      // Skip template modal for question types that don't use templates
      // Instructions are just informational text - no layout complexity needed
      // Yes/No questions are simple binary choices - no complex layout needed
      const skipTemplateTypes = ['text', 'boolean', 'nominal', 'instruction', 'inst', 'yn', 'body_map'];
      if (skipTemplateTypes.includes(questionType)) {
        createAndAddElement();
        return;
      }
      
      // Check if a default template already exists in localAssessmentConfig.default_templates.config.question_templates
      const hasDefaultTemplate = localAssessmentConfig?.default_templates?.config?.question_templates?.[questionType];
      
      console.log('[TEMPLATE] Checking for default template:', {
        questionType,
        hasDefaultTemplate: !!hasDefaultTemplate,
        templateName: hasDefaultTemplate?.template_name,
        availableKeys: Object.keys(localAssessmentConfig?.default_templates?.config?.question_templates || {})
      });
      
      if (hasDefaultTemplate) {
        // Default template exists - skip modal and apply its layout config
        console.log(`[TEMPLATE] ✓ Default EXISTS for ${questionType}, skipping modal, auto-applying template`);
        templateConfig = hasDefaultTemplate.layout_config || null;
        createAndAddElement();
        return;
      }
      
      // Check if any existing questions of this type exist across all screens
      // Normalize both sides so e.g. numeric→rating matches other rating elements
      const hasExistingQuestionOfType = screens.some(screen =>
        screen.elements.some(el =>
          el.type === 'question' &&
          (normalizeQuestionType(el.itemData?.question_type) === questionType ||
           normalizeQuestionType(el.itemData?.type) === questionType)
        )
      );
      
      // If there's already a question of this type, user has already made their choice
      // Just add the element with default positioning (they've already set up the template)
      if (hasExistingQuestionOfType && !autoShowLayoutModal) {
        console.log(`[TEMPLATE] ✓ Existing ${questionType} question found, adding element with default layout`);
        createAndAddElement();
        return;
      }
      
      // This is the FIRST question of this type AND no default template exists
      // Show the modal to let user choose template or blank layout
      console.log(`[TEMPLATE] ✗ First ${questionType} question, no default template, showing modal`);
      setFirstQuestionType(questionType);
      setPendingElement({ itemType, item, customPosition });
      setShowFirstQuestionModal(true);
      return; // Don't add element yet - wait for modal interaction
    }
    
    // Helper function to create and add element
    function createAndAddElement() {
    
    let newElement;
    
    if (itemType === 'question' && item) {
      // Detect instruction items — these are informational text, not questions
      const itemResponseType = item.response_type || item.responseType || item.type || 'ordinal';
      const isInstruction = ['instruction', 'inst'].includes(itemResponseType) ||
                            ['instruction', 'inst'].includes(item.question_type);
      
      if (isInstruction) {
        const defaultPosition = customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 80)) 
        };
        newElement = {
          id: `element_${Date.now()}`,
          type: 'instruction',
          questionId: item.id,
          isLibraryItem: true,
          data: item,
          itemData: item,
          content: item.text || '',
          position: defaultPosition,
          size: { 
            width: Math.min(currentScreen.layout.width - 100, 700), 
            height: 120 
          },
          style: {
            // Seeded from the form-wide formatting defaults (FECOA-732); per-element
            // edits and per-type layout templates still override these.
            fontSize: globalFormatting.baseFontSize,
            fontFamily: globalFormatting.fontFamily,
            fontWeight: '400',
            color: '#1f2937',
            textAlign: 'left',
            lineHeight: globalFormatting.lineSpacing,
            padding: 16,
            backgroundColor: 'transparent',
            borderRadius: 0,
            borderWidth: 0,
            borderColor: 'transparent',
            borderStyle: 'solid'
          }
        };
      } else {
      // Initialize response options from imported item data
      // Check multiple possible locations for response options
      const rawOptions = item.options || item.response_options || [];
      
      // Determine if options is a type-specific config object or an array
      let responseOptions = [];
      let responseConfig = {
        layout: 'vertical',
        spacing: 8,
        columns: 2,
        optionFontSize: 14,
        optionPadding: 8,
        radioSize: 16,
        equalWidth: true
      };
      
      // Handle type-specific options objects
      if (rawOptions && typeof rawOptions === 'object' && !Array.isArray(rawOptions)) {
        if (itemResponseType === 'vas' || rawOptions.type === 'vas') {
          responseConfig = {
            ...responseConfig,
            layout: 'vas',
            leftAnchor: rawOptions.leftAnchor || 'No pain',
            rightAnchor: rawOptions.rightAnchor || 'Worst pain imaginable',
            minValue: rawOptions.minValue ?? 0,
            maxValue: rawOptions.maxValue ?? 100
          };
        } else if (itemResponseType === 'numeric' || itemResponseType === 'rating' || rawOptions.type === 'numeric' || rawOptions.type === 'rating') {
          responseConfig = {
            ...responseConfig,
            layout: 'nrs',
            minValue: rawOptions.minValue ?? 0,
            maxValue: rawOptions.maxValue ?? 10,
            step: rawOptions.step ?? 1,
            leftAnchor: rawOptions.leftAnchor || '',
            rightAnchor: rawOptions.rightAnchor || ''
          };
        } else if (itemResponseType === 'text' || rawOptions.type === 'text') {
          responseConfig = {
            ...responseConfig,
            layout: 'text',
            maxLength: rawOptions.maxLength ?? 500,
            multiline: rawOptions.multiline ?? true
          };
        } else if (itemResponseType === 'boolean' || rawOptions.type === 'boolean') {
          responseConfig = { ...responseConfig, layout: 'boolean' };
          responseOptions = [
            { order: 1, label: rawOptions.yesLabel || 'Yes', value: 1 },
            { order: 2, label: rawOptions.noLabel || 'No', value: 0 }
          ];
        }
      } else if (Array.isArray(rawOptions)) {
        // Even when options is an array (or empty), check itemResponseType for type-specific layouts
        if (itemResponseType === 'text') {
          responseConfig = { ...responseConfig, layout: 'text', maxLength: 500, multiline: true };
        } else if (itemResponseType === 'boolean') {
          responseConfig = { ...responseConfig, layout: 'boolean' };
          responseOptions = [
            { order: 1, label: 'Yes', value: 1 },
            { order: 2, label: 'No', value: 0 }
          ];
        } else if (itemResponseType === 'numeric' || itemResponseType === 'rating') {
          responseConfig = {
            ...responseConfig,
            layout: 'nrs',
            minValue: 0,
            maxValue: 10,
            step: 1,
            leftAnchor: '',
            rightAnchor: ''
          };
        } else if (itemResponseType === 'vas') {
          responseConfig = {
            ...responseConfig,
            layout: 'vas',
            leftAnchor: 'No pain',
            rightAnchor: 'Worst pain imaginable',
            minValue: 0,
            maxValue: 100
          };
        } else {
          responseOptions = rawOptions.map((opt, index) => ({
            order: index + 1,
            label: typeof opt === 'string' ? opt : (opt.label || opt.text || `Option ${index + 1}`),
            value: typeof opt === 'object' && opt.value !== undefined ? opt.value : index
          }));
        }
      }

      // Use the processed responseOptions for height calc since item.options may be raw/empty
      const heightItem = { ...item, options: responseOptions.length > 0 ? responseOptions : item.options };
      const dynamicHeight = calculateQuestionHeight(heightItem, responseConfig);
      
      // ALL question items from the sidebar are library items and should be read-only
      // The sidebar contains pre-defined assessment items that should not be edited in the builder
      const isLibraryItem = true;

      // Apply template config if provided, otherwise use defaults
      const defaultPosition = customPosition || {
        x: snapPosition(50),
        y: snapPosition(50 + (currentScreen.elements.length * 80))
      };
      const maxHeight = currentScreen.layout.height - defaultPosition.y - 20;
      const isBodyMap = itemResponseType === 'body_map';
      const defaultSize = isBodyMap
        ? {
            // Figures container ≈ 448px + 24px gap + panel ≈ 252px + element padding
            width: Math.min(currentScreen.layout.width - 100, 780),
            // Two 600px-tall figures + label + question text + padding ≈ 750px
            // (fits the 800px default canvas at y=50)
            height: 750,
          }
        : {
            width: Math.min(currentScreen.layout.width - 100, 700),
            height: Math.min(dynamicHeight, Math.max(maxHeight, 120))
          };
      const defaultStyle = {
        // Seeded from the form-wide formatting defaults (FECOA-732); per-element
        // edits and per-type layout templates still override these.
        fontSize: globalFormatting.baseFontSize,
        fontFamily: globalFormatting.fontFamily,
        fontWeight: '400',
        color: '#1f2937',
        textAlign: 'left',
        lineHeight: globalFormatting.lineSpacing,
        padding: 16,
        backgroundColor: 'transparent',
        borderRadius: 0,
        borderWidth: 0,
        borderColor: 'transparent',
        borderStyle: 'solid'
      };
      
      // Extract position and size from template config if available
      let elementPosition = defaultPosition;
      let elementSize = defaultSize;
      let elementStyle = defaultStyle;
      
      console.log('[ADD_ELEMENT] Default position:', defaultPosition);
      console.log('[ADD_ELEMENT] Default size:', defaultSize);
      console.log('[ADD_ELEMENT] Template config:', templateConfig);
      
      if (templateConfig) {
        // Apply template position if available
        if (templateConfig.position) {
          elementPosition = {
            x: templateConfig.position.x ?? defaultPosition.x,
            y: templateConfig.position.y ?? defaultPosition.y
          };
          console.log('[ADD_ELEMENT] Applied template position:', elementPosition);
          
          // Apply template size if available, but ensure height fits content
          if (templateConfig.position.width && templateConfig.position.height) {
            elementSize = {
              width: templateConfig.position.width,
              height: Math.max(templateConfig.position.height, dynamicHeight)
            };
            console.log('[ADD_ELEMENT] Applied template size (height clamped to content minimum):', elementSize);
          }
        }
        
        // Apply template style if available
        if (templateConfig.style) {
          elementStyle = {
            ...defaultStyle,
            ...templateConfig.style
          };
          console.log('[ADD_ELEMENT] Applied template style:', elementStyle);
        }
        
        // Apply template response config if available. Newer saved templates
        // store this under responseConfig; older/system templates store the same
        // layout settings under responseOptions.
        const templateResponseConfig = templateConfig.responseConfig
          || (!Array.isArray(templateConfig.responseOptions) ? templateConfig.responseOptions : null);
        if (templateResponseConfig) {
          // Map display styles to layout values the renderer expects
          let layoutValue = responseConfig.layout;
          if (templateResponseConfig.display) {
            const displayStyle = templateResponseConfig.display;
            // Check slider patterns BEFORE generic horizontal/vertical
            if (displayStyle.includes('slider')) {
              layoutValue = templateResponseConfig.continuous ? 'vas' : 'nrs';
            } else if (displayStyle.includes('horizontal')) {
              layoutValue = 'horizontal';
            } else if (displayStyle.includes('vertical')) {
              layoutValue = 'vertical';
            } else if (displayStyle.includes('grid')) {
              layoutValue = 'grid';
            } else if (displayStyle === 'dropdown') {
              layoutValue = 'dropdown';
            }
          }
          
          responseConfig = {
            ...responseConfig,
            ...templateResponseConfig,
            layout: layoutValue
          };
          console.log('[ADD_ELEMENT] Applied template responseConfig:', responseConfig);
        }
      }
      
      console.log('[ADD_ELEMENT] Final element position:', elementPosition);
      console.log('[ADD_ELEMENT] Final element size:', elementSize);
      
      newElement = {
        id: `element_${Date.now()}`,
        type: 'question',
        questionId: item.id,
        isLibraryItem: isLibraryItem,
        data: {
          ...item,
          options: responseOptions
        },
        itemData: item,
        content: item.text || '',
        responseOptions: responseOptions,
        position: elementPosition,
        size: elementSize,
        style: elementStyle,
        responseConfig: responseConfig,
        required: templateConfig?.is_required || false,
      };
      } // end else (non-instruction question)
    } else if (itemType === 'button') {
      // Add button element
      newElement = {
        id: `element_${Date.now()}`,
        type: 'button',
        content: 'Next',
        buttonType: 'next', // 'next', 'back', 'submit', 'custom'
        position: customPosition || { 
          x: snapPosition(currentScreen.layout.width - 150), 
          y: snapPosition(currentScreen.layout.height - 100) 
        },
        size: { 
          width: 120, 
          height: 44 
        },
        style: {
          fontSize: 16,
          fontFamily: 'Arial, sans-serif',
          fontWeight: '600',
          color: '#ffffff',
          textAlign: 'center',
          backgroundColor: '#0d9488',
          borderRadius: 8,
          borderWidth: 0,
          borderColor: '#0d9488',
          borderStyle: 'solid',
          padding: 12
        }
      };
    } else if (itemType === 'divider') {
      newElement = {
        id: `element_${Date.now()}`,
        type: 'divider',
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { width: currentScreen.layout.width - 100, height: 2 },
        style: {
          backgroundColor: '#d1d5db',
          borderRadius: 0
        },
        config: {
          thickness: 2,
          style: 'solid' // solid, dashed, dotted
        }
      };
    } else if (itemType === 'text-input') {
      newElement = {
        id: `element_${Date.now()}`,
        type: 'text-input',
        label: 'Text Input Label',
        placeholder: 'Enter text here...',
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { width: 400, height: 80 },
        style: {
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          color: '#000000'
        },
        config: {
          multiline: false,
          maxLength: null,
          required: false
        }
      };
    } else if (itemType === 'number-input') {
      newElement = {
        id: `element_${Date.now()}`,
        type: 'number-input',
        label: 'Number Input Label',
        placeholder: 'Enter number...',
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { width: 300, height: 80 },
        style: {
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          color: '#000000'
        },
        config: {
          min: null,
          max: null,
          step: 1,
          required: false
        }
      };
    } else if (itemType === 'date-picker') {
      newElement = {
        id: `element_${Date.now()}`,
        type: 'date-picker',
        label: 'Date Picker Label',
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { width: 300, height: 80 },
        style: {
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          color: '#000000'
        },
        config: {
          includeTime: false,
          minDate: null,
          maxDate: null,
          required: false
        }
      };
    } else if (itemType === 'checkbox') {
      newElement = {
        id: `element_${Date.now()}`,
        type: 'checkbox',
        label: 'Select all that apply:',
        options: ['Option 1', 'Option 2', 'Option 3'],
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { width: 400, height: 150 },
        style: {
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          color: '#000000'
        },
        config: {
          layout: 'vertical',
          spacing: 8,
          required: false,
          minSelections: null,
          maxSelections: null
        }
      };
    } else if (itemType === 'scale') {
      newElement = {
        id: `element_${Date.now()}`,
        type: 'scale',
        label: 'Rate your experience',
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { width: 500, height: 100 },
        style: {
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          color: '#000000'
        },
        config: {
          min: 0,
          max: 10,
          step: 1,
          showLabels: true,
          minLabel: 'Not at all',
          maxLabel: 'Extremely',
          showValue: true,
          required: false
        }
      };
    } else {
      // Add other element types (text, image, etc.)
      newElement = {
        id: `element_${Date.now()}`,
        type: itemType,
        content: itemType === 'text' ? 'New Text' : '',
        position: customPosition || { 
          x: snapPosition(50), 
          y: snapPosition(50 + (currentScreen.elements.length * 60)) 
        },
        size: { 
          width: 400, 
          height: itemType === 'text' ? 40 : 200 
        },
        style: {
          fontSize: 16,
          fontFamily: 'Arial, sans-serif',
          fontWeight: '400',
          color: '#000000',
          textAlign: 'left',
          lineHeight: 1.5,
          padding: 8,
          backgroundColor: 'transparent'
        }
      };
    }

    const updatedScreen = {
      ...currentScreen,
      elements: [...currentScreen.elements, newElement]
    };

    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = updatedScreen;
    
    setScreens(updatedScreens);
    setSelectedElement(newElement.id);
    };
    
    // Call the helper if not checking templates
    createAndAddElement();
  };

  // Handle adding item from Item Library
  const handleAddItemFromBank = (itemConfigOrArray) => {
    // Handle both single item and array of items
    const itemConfigs = Array.isArray(itemConfigOrArray) ? itemConfigOrArray : [itemConfigOrArray];
    
    console.log(`[SCREEN BUILDER] Adding ${itemConfigs.length} items from Item Library`, itemConfigs);
    
    const questionItems = itemConfigs.map(itemConfig => {
      // Convert Item Library item to question format
      return {
        id: itemConfig.itemData.id,
        text: itemConfig.itemData.text,
        code: itemConfig.itemData.code,
        domain: itemConfig.itemData.domain,
        response_type: itemConfig.itemData.response_type || itemConfig.itemData.responseType,
        response_options: itemConfig.itemData.response_options || itemConfig.itemData.responseOptions || itemConfig.responseOptions,
        options: itemConfig.itemData.response_options || itemConfig.itemData.responseOptions || itemConfig.responseOptions,
        source: itemConfig.itemData.source,
        validated: itemConfig.itemData.validated,
        item_bank_id: itemConfig.itemData.item_bank_id || itemConfig.itemData.id,
        question_type: normalizeQuestionType(itemConfig.itemData.response_type || itemConfig.itemData.responseType),
        category: itemConfig.itemData.category,
        tags: itemConfig.itemData.tags,
        content_hash: itemConfig.itemData.content_hash,
      };
    });

    // Add to items list (avoid duplicates)
    const newItems = questionItems.filter(qi => !items.find(item => item.id === qi.id));
    if (newItems.length > 0) {
      setItems([...items, ...newItems]);
      console.log(`[SCREEN BUILDER] Added ${newItems.length} items to sidebar (not auto-placed on screen)`);
    }

    // Don't auto-add to screen - let user drag them onto screens manually
    // The items are now available in the sidebar for dragging
  };

  // Inline editing handlers
  const handleDoubleClick = (e, elementId) => {
    e.stopPropagation();
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element) return;
    
    // Only allow inline editing for text and question elements
    // Library items (questions from sidebar) should NOT be editable
    if (element.type === 'text') {
      setEditingElement(elementId);
      setEditingValue(element.content || '');
    } else if (element.type === 'question' && !element.isLibraryItem) {
      setEditingElement(elementId);
      setEditingValue(element.data?.text || '');
    } else if (element.type === 'button') {
      setEditingElement(elementId);
      setEditingValue(element.content || '');
    }
  };

  const handleInlineEditSave = (elementId) => {
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element) return;

    const updatedElements = currentScreen.elements.map(el => {
      if (el.id === elementId) {
        if (el.type === 'text' || el.type === 'button') {
          return { ...el, content: editingValue };
        } else if (el.type === 'question') {
          return { 
            ...el, 
            data: { ...el.data, text: editingValue }
          };
        }
      }
      return el;
    });

    const updatedScreen = { ...currentScreen, elements: updatedElements };
    const updatedScreens = [...screens];
    updatedScreens[currentScreenIndex] = updatedScreen;
    setScreens(updatedScreens);

    setEditingElement(null);
    setEditingValue('');
  };

  const handleInlineEditCancel = () => {
    setEditingElement(null);
    setEditingValue('');
  };

  // Drag and drop handlers
  const handleMouseDown = (e, elementId) => {
    console.debug('🖱️ [MOUSE DOWN] Element drag started:', elementId);
    // Don't prevent default or start dragging if we're in edit mode
    if (editingElement === elementId) {
      console.debug('⚠️ [MOUSE DOWN] In edit mode, skipping');
      return;
    }
    
    e.preventDefault();

    // Additive selection (FECOA-1523): Shift/Ctrl/Cmd+click toggles membership
    // and never starts a drag, keeping toggle-vs-drag unambiguous for the MVP.
    // Locked elements may still be toggled into a selection (just not moved).
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      toggleElementInSelection(elementId);
      return;
    }

    // Check if element is locked
    if (isElementLocked(elementId)) {
      console.debug('🔒 [MOUSE DOWN] Element is locked');
      selectSingleElement(elementId);
      return;
    }

    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element) {
      console.debug('❌ [MOUSE DOWN] Element not found');
      return;
    }

    console.debug('✅ [MOUSE DOWN] Setting dragging state, zoom:', canvasZoom);

    // Group drag: clicking (no modifier) an element that is part of a
    // multi-selection drags the whole group rigidly instead of collapsing to
    // one. Capture each unlocked member's offset from the anchor so the group
    // moves together; locked members are excluded and never move.
    const isGroupDrag = selectedElements.size > 1 && selectedElements.has(elementId);
    if (isGroupDrag) {
      const anchorPos = element.position;
      const offsets = {};
      currentScreen.elements.forEach((el) => {
        if (selectedElements.has(el.id) && !isElementLocked(el.id)) {
          offsets[el.id] = { dx: el.position.x - anchorPos.x, dy: el.position.y - anchorPos.y };
        }
      });
      groupDragOffsetsRef.current = offsets;
      setSelectedElement(elementId); // move the anchor without collapsing the set
    } else {
      groupDragOffsetsRef.current = null;
      selectSingleElement(elementId);
    }

    beginInteractionBatch();
    setDragging(elementId);

    // Calculate offset from element's top-left corner to mouse position
    // Account for canvas zoom when calculating offset
    const canvas = document.getElementById('canvas-area');
    const canvasRect = canvas.getBoundingClientRect();
    const rect = e.currentTarget.getBoundingClientRect();

    setDragOffset({
      x: (e.clientX - rect.left) / canvasZoom,
      y: (e.clientY - rect.top) / canvasZoom
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging) return;
    console.debug('🔄 [MOUSE MOVE] Dragging element:', dragging);

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Schedule update on next animation frame (throttled to ~60fps)
    rafIdRef.current = requestAnimationFrame(() => {
      const canvas = document.getElementById('canvas-area');
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      const activeScreen = screens[currentScreenIndex];

      if (!activeScreen) return;

      // Calculate new position relative to canvas, accounting for zoom
      const newX = (e.clientX - canvasRect.left) / canvasZoom - dragOffset.x;
      const newY = (e.clientY - canvasRect.top) / canvasZoom - dragOffset.y;

      // Group drag (FECOA-1523): move every captured member rigidly, clamping
      // the group bounding box (not each element) so members keep their relative
      // offsets at the canvas edges. One batched write = one history entry.
      if (groupDragOffsetsRef.current) {
        const updates = computeGroupDragPositions({
          elements: activeScreen.elements,
          offsets: groupDragOffsetsRef.current,
          desiredAnchor: { x: newX, y: newY },
          layout: activeScreen.layout,
          snap: snapPosition,
        });
        updateElementPositions(updates);
        return;
      }

      // Clamp so the entire element stays inside the canvas. If the element
      // is wider/taller than the canvas (e.g. an oversized NRS), pin it at 0
      // rather than letting it drag off the left/top edge.
      const draggedEl = activeScreen.elements.find(el => el.id === dragging);
      const elW = draggedEl?.size?.width ?? 0;
      const elH = draggedEl?.size?.height ?? 0;
      // The responsive canvas is designed on a fixed artboard at its nominal
      // design width (it only scales to fill the screen at render time), so
      // dragging clamps to layout.width just like a fixed device canvas. This
      // keeps the design coordinate system stable and unambiguous (FECOA-1532).
      const maxX = Math.max(0, activeScreen.layout.width - elW);
      const maxY = Math.max(0, activeScreen.layout.height - elH);

      updateElementPosition(dragging, {
        x: snapPosition(Math.max(0, Math.min(newX, maxX))),
        y: snapPosition(Math.max(0, Math.min(newY, maxY)))
      });
    });
  }, [dragging, dragOffset, currentScreenIndex, updateElementPosition, updateElementPositions, snapPosition, canvasZoom]);

  const handleMouseUp = () => {
    console.debug('🛑 [MOUSE UP] Drag ended for element:', dragging);
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setDragging(null);
    groupDragOffsetsRef.current = null;
    endInteractionBatch();
  };

  // Sidebar resize handlers
  const handleSidebarResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleSidebarResize = useCallback((e) => {
    if (!isResizing) return;
    
    let newWidth;
    if (sidebarPosition === 'left') {
      newWidth = e.clientX;
    } else if (sidebarPosition === 'right') {
      newWidth = window.innerWidth - e.clientX;
    }
    
    // Calculate max width as 50% of screen width
    const maxWidthByScreen = window.innerWidth * 0.5;
    const absoluteMax = Math.min(600, maxWidthByScreen);
    
    // Clamp width between 280px and the calculated max
    if (newWidth) {
      newWidth = Math.max(320, Math.min(absoluteMax, newWidth));
      setSidebarWidth(newWidth);
    }
  }, [isResizing, sidebarPosition]);

  const handleSidebarResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove resize event listeners
  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleSidebarResize);
      document.addEventListener('mouseup', handleSidebarResizeEnd);
      return () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleSidebarResize);
        document.removeEventListener('mouseup', handleSidebarResizeEnd);
      };
    }
  }, [isResizing, handleSidebarResize, handleSidebarResizeEnd]);

  // Adjust sidebar width when window is resized to prevent overflow
  useEffect(() => {
    let resizeTimer;
    const handleWindowResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const maxWidthByScreen = window.innerWidth * 0.5;
        const absoluteMax = Math.min(600, maxWidthByScreen);
        
        // If current sidebar width exceeds the max for this screen size, adjust it
        if (sidebarWidth > absoluteMax) {
          setSidebarWidth(Math.max(320, absoluteMax));
        }
        
        // On tablet screens (768-1024px), limit to 40% of width
        if (window.innerWidth >= 768 && window.innerWidth < 1024) {
          const tabletMax = Math.min(sidebarWidth, window.innerWidth * 0.4);
          if (sidebarWidth > tabletMax) {
            setSidebarWidth(Math.max(320, tabletMax));
          }
        }
        
        // On very small screens, ensure minimum usability
        if (window.innerWidth < 768) {
          const mobileMax = Math.min(window.innerWidth * 0.85, 350);
          if (sidebarWidth > mobileMax) {
            setSidebarWidth(Math.max(320, mobileMax));
          }
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(resizeTimer);
    };
  }, [sidebarWidth, sidebarCollapsed]);

  // Resize handlers
  const handleResizeStart = (e, elementId, handle) => {
    e.stopPropagation();
    e.preventDefault();
    
    const element = currentScreen.elements.find(el => el.id === elementId);
    if (!element) return;
    
    beginInteractionBatch();
    setResizing({
      elementId,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: element.size.width,
      startHeight: element.size.height,
      startPosX: element.position.x,
      startPosY: element.position.y
    });
    setSelectedElement(elementId);
  };

  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    
    // Cancel any pending animation frame
    if (rafIdResizeRef.current) {
      cancelAnimationFrame(rafIdResizeRef.current);
    }

    // Schedule update on next animation frame (throttled to ~60fps)
    rafIdResizeRef.current = requestAnimationFrame(() => {
      const { elementId, handle, startX, startY, startWidth, startHeight, startPosX, startPosY } = resizing;
      
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      let newPosX = startPosX;
      let newPosY = startPosY;
      
      // Calculate new dimensions based on handle
      switch (handle) {
        case 'se': // Bottom-right corner
          newWidth = Math.max(50, startWidth + deltaX);
          newHeight = Math.max(30, startHeight + deltaY);
          break;
        case 'sw': // Bottom-left corner
          newWidth = Math.max(50, startWidth - deltaX);
          newHeight = Math.max(30, startHeight + deltaY);
          newPosX = startPosX + (startWidth - newWidth);
          break;
        case 'ne': // Top-right corner
          newWidth = Math.max(50, startWidth + deltaX);
          newHeight = Math.max(30, startHeight - deltaY);
          newPosY = startPosY + (startHeight - newHeight);
          break;
        case 'nw': // Top-left corner
          newWidth = Math.max(50, startWidth - deltaX);
          newHeight = Math.max(30, startHeight - deltaY);
          newPosX = startPosX + (startWidth - newWidth);
          newPosY = startPosY + (startHeight - newHeight);
          break;
        case 'e': // Right edge
          newWidth = Math.max(50, startWidth + deltaX);
          break;
        case 'w': // Left edge
          newWidth = Math.max(50, startWidth - deltaX);
          newPosX = startPosX + (startWidth - newWidth);
          break;
        case 's': // Bottom edge
          newHeight = Math.max(30, startHeight + deltaY);
          break;
        case 'n': // Top edge
          newHeight = Math.max(30, startHeight - deltaY);
          newPosY = startPosY + (startHeight - newHeight);
          break;
      }

      // Clamp against canvas bounds. For right/bottom handles, cap the size.
      // For left/top handles, the position has already moved with the size
      // change, so we cap newPosX/newPosY ≥ 0 and shrink newWidth/newHeight
      // back to fit if the user dragged past the edge.
      const canvasW = currentScreen?.layout?.width;
      const canvasH = currentScreen?.layout?.height;
      if (canvasW) {
        if (newPosX < 0) {
          newWidth = Math.max(50, newWidth + newPosX);
          newPosX = 0;
        }
        if (newPosX + newWidth > canvasW) {
          newWidth = Math.max(50, canvasW - newPosX);
        }
      }
      if (canvasH) {
        if (newPosY < 0) {
          newHeight = Math.max(30, newHeight + newPosY);
          newPosY = 0;
        }
        if (newPosY + newHeight > canvasH) {
          newHeight = Math.max(30, canvasH - newPosY);
        }
      }
      
      // Apply snap to grid if enabled
      if (snapToGrid) {
        newWidth = snapPosition(newWidth);
        newHeight = snapPosition(newHeight);
        newPosX = snapPosition(newPosX);
        newPosY = snapPosition(newPosY);
      }
      
      // Update element using functional update
      setScreens(prevScreens => prevScreens.map((screen, idx) => {
        if (idx === currentScreenIndex) {
          return {
            ...screen,
            elements: screen.elements.map(el =>
              el.id === elementId
                ? { 
                    ...el, 
                    size: { width: newWidth, height: newHeight },
                    position: { x: newPosX, y: newPosY }
                  }
                : el
            )
          };
        }
        return screen;
      }));
    });
  }, [resizing, currentScreenIndex, snapPosition, snapToGrid]);

  const handleResizeEnd = () => {
    // Cancel any pending animation frame
    if (rafIdResizeRef.current) {
      cancelAnimationFrame(rafIdResizeRef.current);
      rafIdResizeRef.current = null;
    }
    setResizing(null);
    endInteractionBatch();
  };

  // Add global mouse move and up listeners for dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, dragOffset, screens, currentScreenIndex, handleMouseMove]);

  // Add global mouse move and up listeners for resizing
  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizing, screens, currentScreenIndex, snapToGrid, gridSize, handleResizeMove]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts if user is typing in an input/textarea or contenteditable
      const isEditing = e.target.tagName === 'INPUT' || 
                        e.target.tagName === 'TEXTAREA' || 
                        e.target.isContentEditable ||
                        e.target.closest('input') ||
                        e.target.closest('textarea');
      
      if (isEditing) return;

      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'z' && !e.shiftKey) {
        if (canUndo) {
          e.preventDefault();
          undoHistory();
        }
        return;
      }
      if (mod && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        if (canRedo) {
          e.preventDefault();
          redoHistory();
        }
        return;
      }

      // Copy selected element(s) to an in-memory clipboard (FECOA-1523)
      if (mod && (e.key === 'c' || e.key === 'C') && selectedElements.size > 0) {
        const currentScreen = screens[currentScreenIndex];
        const copied = (currentScreen?.elements || []).filter(el => selectedElements.has(el.id));
        if (copied.length) {
          clipboardRef.current = copied.map(el => structuredClone(el));
          e.preventDefault();
        }
        return;
      }

      // Paste clipboard element(s) onto the current screen, offset so they don't
      // sit exactly on top of the originals. New ids are minted and backend
      // linkage (screen_question_id) is stripped so the save layer treats them as
      // brand-new questions. Pasted copies become the active selection.
      if (mod && (e.key === 'v' || e.key === 'V') && clipboardRef.current?.length) {
        e.preventDefault();
        const currentScreen = screens[currentScreenIndex];
        if (!currentScreen) return;
        const offset = 20;
        const stamp = Date.now();
        const layoutW = currentScreen.layout?.width ?? Infinity;
        const layoutH = currentScreen.layout?.height ?? Infinity;
        const pasted = clipboardRef.current.map((el, i) => {
          const clone = structuredClone(el);
          delete clone.screen_question_id;
          delete clone.survey_screen_id;
          const w = clone.size?.width ?? 0;
          const h = clone.size?.height ?? 0;
          return {
            ...clone,
            id: `element_${stamp}_${i}`,
            isLocked: false,
            position: {
              x: Math.max(0, Math.min((clone.position?.x ?? 0) + offset, Math.max(0, layoutW - w))),
              y: Math.max(0, Math.min((clone.position?.y ?? 0) + offset, Math.max(0, layoutH - h))),
            },
          };
        });
        setScreens(screens.map((screen, index) =>
          index === currentScreenIndex
            ? { ...screen, elements: [...screen.elements, ...pasted] }
            : screen
        ));
        const pastedIds = pasted.map(el => el.id);
        setSelectedElements(new Set(pastedIds));
        setSelectedElement(pastedIds[pastedIds.length - 1] || null);
        return;
      }

      // Delete or Backspace to remove selected element(s)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElements.size > 0) {
        e.preventDefault();
        deleteSelectedElements();
      }

      // Arrow keys to move the selected element(s)
      if (selectedElements.size > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const currentScreen = screens[currentScreenIndex];
        if (!currentScreen) return;
        // Only unlocked members move.
        const ids = [...selectedElements].filter(id => !lockedElements.has(id));
        if (ids.length === 0) return;

        const moveDistance = e.shiftKey ? 10 : 1; // Hold Shift for faster movement
        let dx = 0;
        let dy = 0;
        switch (e.key) {
          case 'ArrowUp': dy = -moveDistance; break;
          case 'ArrowDown': dy = moveDistance; break;
          case 'ArrowLeft': dx = -moveDistance; break;
          case 'ArrowRight': dx = moveDistance; break;
        }

        const updates = computeGroupNudgePositions({
          elements: currentScreen.elements,
          ids,
          dx,
          dy,
          layout: currentScreen.layout,
        });
        if (updates.length) {
          updateElementPositions(updates);
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedElement,
    selectedElements,
    screens,
    currentScreenIndex,
    lockedElements,
    updateElementPositions,
    canUndo,
    canRedo,
    undoHistory,
    redoHistory,
  ]);

  // Clear selection / inline edit when undo/redo or edits remove the element from the active screen
  useEffect(() => {
    const screen = screens[currentScreenIndex];
    if (editingElement != null) {
      const editingOk = screen?.elements?.some((el) => el.id === editingElement);
      if (!editingOk) {
        setEditingElement(null);
        setEditingValue('');
      }
    }
    if (selectedElement != null) {
      const selOk = screen?.elements?.some((el) => el.id === selectedElement);
      if (!selOk) setSelectedElement(null);
    }
    // Prune multi-selection to ids still present on the active screen. This also
    // clears the set on screen switch (no ids carry over) — FECOA-1523.
    setSelectedElements((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(screen?.elements?.map((el) => el.id) || []);
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (present.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [screens, currentScreenIndex, selectedElement, editingElement]);

  // Keep the selection set in sync with the anchor for single-select entry
  // points that only call setSelectedElement (add-element, preview, template
  // reselect, etc.). Additive multi-select sets both at once and never collapses
  // here because the set already contains the new anchor (FECOA-1523).
  useEffect(() => {
    setSelectedElements((prev) => {
      if (selectedElement == null) return prev.size === 0 ? prev : new Set();
      if (prev.has(selectedElement)) return prev; // already consistent (incl. multi)
      return new Set([selectedElement]); // fresh single selection collapses the set
    });
  }, [selectedElement]);

  // Load locked elements from screen data on initialization
  useEffect(() => {
    const newLockedElements = new Set();
    screens.forEach(screen => {
      screen.elements?.forEach(element => {
        if (element.isLocked) {
          newLockedElements.add(element.id);
        }
      });
    });
    setLockedElements(newLockedElements);
  }, [screens.length]); // Only run when screens are loaded/changed in number

  // Auto-switch to Properties panel when a NEW element is selected (only from Elements tab)
  // Use a ref to track the previous selection to avoid switching when user manually changes tabs
  const prevSelectedElement = React.useRef(null);
  const activePanelRef = React.useRef(activePanel);
  activePanelRef.current = activePanel; // Keep ref in sync
  
  useEffect(() => {
    // Only auto-switch if a NEW element was selected (not when user switches tabs)
    // Use ref for activePanel to avoid re-running effect when tab changes
    if (selectedElement && selectedElement !== prevSelectedElement.current && !sidebarCollapsed && activePanelRef.current === 'elements') {
      setActivePanel('properties');
    }
    prevSelectedElement.current = selectedElement;
  }, [selectedElement, sidebarCollapsed]); // Removed activePanel from deps

  // Load navigation rules when a question is selected
  useEffect(() => {
    const loadRulesForSelectedQuestion = async () => {
      if (!selectedElement || !screens[currentScreenIndex]) {
        return;
      }

      // Find the selected element
      const selectedElementData = screens[currentScreenIndex]?.elements?.find(
        el => el.id === selectedElement
      );

      // Only load rules for questions with a screen_question_id
      if (selectedElementData?.type === 'question' && selectedElementData?.screen_question_id) {
        // Check if we already have rules loaded for this question
        if (!navigationRules[selectedElement]) {
          try {
            await loadNavigationRulesForQuestion(selectedElementData.screen_question_id, selectedElement);
          } catch (error) {
            console.error('Error auto-loading navigation rules:', error);
            // Initialize with empty rules on error
            setNavigationRules(prev => ({
              ...prev,
              [selectedElement]: []
            }));
          }
        }
      }
    };

    loadRulesForSelectedQuestion();
  }, [selectedElement, currentScreenIndex, screens]);

  // Load all navigation rules on initial assessment load (for Navigation panel display)
  useEffect(() => {
    const loadAllRulesOnMount = async () => {
      // Only run if we have screens with elements that have screen_question_ids
      const hasScreenQuestionIds = screens.some(screen => 
        screen.elements?.some(el => el.type === 'question' && el.screen_question_id)
      );
      
      if (!hasScreenQuestionIds || Object.keys(navigationRules).length > 0) {
        return; // Skip if no SQ IDs yet or rules already loaded
      }
      
      console.debug('[Initial Load] Loading all navigation rules for display...');
      await loadAllNavigationRulesForPreview();
    };
    
    loadAllRulesOnMount();
  }, [screens]); // Re-run when screens change (e.g., after SQ mapping update)

  // Populate rule form when editing a rule
  useEffect(() => {
    if (editingRule) {
      // Normalize target_screen_id to match dropdown values
      // The backend stores database survey_screen_id, but dropdown uses frontend screen.id
      let normalizedTargetScreenId = editingRule.target_screen_id;
      
      if (normalizedTargetScreenId && editingRule.action_type === 'skip_to') {
        // Try to find the screen by various ID formats
        let targetScreen = screens.find(s => String(s.id) === String(normalizedTargetScreenId));
        
        // If not found by frontend id, try by database survey_screen_id
        if (!targetScreen) {
          targetScreen = screens.find(s => String(s.survey_screen_id) === String(normalizedTargetScreenId));
        }
        
        // If not found by ID, try by target_screen_index
        if (!targetScreen && editingRule.target_screen_index !== undefined && editingRule.target_screen_index >= 0) {
          targetScreen = screens[editingRule.target_screen_index];
        }
        
        // If not found by index, try by target_screen_name
        if (!targetScreen && editingRule.target_screen_name) {
          targetScreen = screens.find(s => s.name === editingRule.target_screen_name);
        }
        
        // Update to use the frontend screen.id for the dropdown
        if (targetScreen) {
          normalizedTargetScreenId = targetScreen.id;
        }
      }
      
      setRuleFormData({
        ...editingRule,
        target_screen_id: normalizedTargetScreenId
      });
    } else if (!showRuleEditorModal) {
      // Reset form when modal closes
      setRuleFormData({
        condition_type: '',
        operator: '>=',
        value: '',
        question_ids: [],
        action_type: '',
        target_screen_id: '',
        alert_message: '',
        enabled: true
      });
    }
  }, [editingRule, showRuleEditorModal, screens]);

  // TEMPORARILY DISABLED: Skip Logic Templates - see state declaration for details
  // // Load navigation templates when Template Picker Modal opens
  // useEffect(() => {
  //   if (showTemplatePickerModal) {
  //     setLoadingTemplates(true);
  //     loadNavigationTemplates()
  //       .then(templates => {
  //         setNavigationTemplates(templates);
  //         setLoadingTemplates(false);
  //       })
  //       .catch(error => {
  //         console.error('Error loading templates:', error);
  //         setLoadingTemplates(false);
  //       });
  //   } else {
  //     // Reset selection when modal closes
  //     setSelectedTemplate(null);
  //   }
  // }, [showTemplatePickerModal]);

  // Canvas panning with right-click only (doesn't interfere with element dragging)
  const handleCanvasPanStart = (e) => {
    // Only pan on right-click (button 2)
    if (e.button !== 2) return;
    
    e.preventDefault();
    setIsPanning(true);
    setPanStart({ x: e.clientX - canvasPan.x, y: e.clientY - canvasPan.y });
  };

  const handleCanvasPanMove = useCallback((e) => {
    if (!isPanning) return;
    
    e.preventDefault();
    setCanvasPan({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y
    });
  }, [isPanning, panStart]);

  const handleCanvasPanEnd = () => {
    setIsPanning(false);
  };

  // Add/remove canvas pan event listeners
  useEffect(() => {
    if (isPanning) {
      window.addEventListener('mousemove', handleCanvasPanMove);
      window.addEventListener('mouseup', handleCanvasPanEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleCanvasPanMove);
        window.removeEventListener('mouseup', handleCanvasPanEnd);
      };
    }
  }, [isPanning, handleCanvasPanMove]);

  // Clear selection when switching screens or if selected element no longer exists
  useEffect(() => {
    const currentScreen = screens[currentScreenIndex];
    if (selectedElement && currentScreen) {
      const elementExists = currentScreen.elements.some(el => el.id === selectedElement);
      if (!elementExists) {
        setSelectedElement(null);
      }
    }
  }, [currentScreenIndex, screens, selectedElement]);

  // Device fit validation - check if elements exceed device bounds (JIRA-699)
  useEffect(() => {
    if (!screens.length || !deviceDimensions[deviceMode]) return;
    
    const dims = deviceDimensions[deviceMode];
    const warnings = [];
    
    screens.forEach((screen, screenIndex) => {
      if (!screen.elements) return;
      
      screen.elements.forEach(element => {
        const rightEdge = element.position.x + element.size.width;
        const bottomEdge = element.position.y + element.size.height;
        
        if (rightEdge > dims.width || bottomEdge > dims.height || 
            element.position.x < 0 || element.position.y < 0) {
          warnings.push({
            screenIndex,
            screenName: screen.name,
            elementId: element.id,
            elementType: element.type,
            issue: rightEdge > dims.width ? 'extends beyond right edge' :
                   bottomEdge > dims.height ? 'extends beyond bottom edge' :
                   element.position.x < 0 ? 'positioned off left edge' :
                   'positioned off top edge',
            overflow: {
              right: Math.max(0, rightEdge - dims.width),
              bottom: Math.max(0, bottomEdge - dims.height)
            }
          });
        }
      });
    });
    
    setDeviceFitWarnings(warnings);
  }, [screens, deviceMode]);

  // Persist a button config into AssessmentTemplate.button_templates[deviceMode]
  const persistButtonTemplateToAssessment = async (buttonConfig) => {
    if (!assessmentConfig?.assessment_uuid) return;
    try {
      await api.post('/api/assessment-builder/screen-templates/set-assessment-default/', {
        assessment_id: assessmentConfig.assessment_uuid,
        device_mode: deviceMode,
        button_config: buttonConfig,
      });
    } catch (err) {
      console.error('[BUTTON_TEMPLATE] Failed to persist to AssessmentTemplate:', err);
    }
  };

  // Button Layout Template Functions
  const fetchButtonTemplates = async () => {
    try {
      const response = await api.get(
        `/api/assessment-builder/screen-templates/?assessment_id=${assessmentConfig.assessment_uuid}&device_mode=${deviceMode}`
      );
      const data = response.data;
      setButtonTemplates([...data.system, ...data.sponsor, ...(data.user || []), ...data.assessment]);
    } catch (error) {
      console.error('Failed to fetch button templates:', error);
    }
  };

  const handleSaveButtonTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const currentScreen = screens[currentScreenIndex];
    const buttons = currentScreen.elements.filter(el => el.type === 'button');
    
    if (buttons.length === 0) {
      toast.error('No buttons on current screen to save');
      return;
    }

    const buttonConfig = {};
    buttons.forEach(btn => {
      const key = btn.buttonType === 'back' ? 'back_button' : 'next_button';
      buttonConfig[key] = {
        x: btn.position.x,
        y: btn.position.y,
        width: btn.size.width,
        height: btn.size.height,
        style: btn.style,
        buttonType: btn.buttonType,
        content: btn.content
      };
    });
    
    try {
      const response = await api.post('/api/assessment-builder/screen-templates/create/', {
        name: newTemplateName,
        level: templateSaveLevel,
        ...(templateSaveLevel === 'assessment' && { assessment_id: assessmentConfig.assessment_uuid }),
        device_mode: deviceMode,
        button_config: buttonConfig,
        is_default: setTemplateAsDefault
      });
      
      const savedLevel = templateSaveLevel === 'user' ? 'your personal templates' : 'this assessment';
      toast.success(`Button template "${newTemplateName}" saved to ${savedLevel}!`);
      setNewTemplateName('');
      setSetTemplateAsDefault(false);
      fetchButtonTemplates();

      // If set as default → also persist to AssessmentTemplate.button_templates
      if (setTemplateAsDefault) {
        await persistButtonTemplateToAssessment(buttonConfig);
      }
    } catch (error) {
      console.error('Failed to save button template:', error);
      const errorMsg = error.response?.data?.error || 'Failed to save template';
      toast.error(errorMsg);
    }
  };

  const handleDeleteButtonTemplate = async (templateId) => {
    if (!templateId) return;
    
    try {
      await api.delete(`/api/assessment-builder/screen-templates/${templateId}/`);
      toast.success('Template deleted successfully');
      setSelectedButtonTemplate('');
      fetchButtonTemplates();
    } catch (error) {
      console.error('Failed to delete button template:', error);
      const errorMsg = error.response?.data?.error || 'Failed to delete template';
      toast.error(errorMsg);
    }
  };

  const handleLoadButtonTemplate = async (e) => {
    const templateId = e.target.value;
    setSelectedButtonTemplate(templateId);
    
    if (!templateId) return;
    
    const template = buttonTemplates.find(t => t.id == templateId);
    
    if (template?.button_config) {
      applyButtonTemplate(template.button_config);
      toast.success(`Applied "${template.name}" button layout`);
    }
  };

  const applyButtonTemplate = (buttonConfig) => {
    const currentScreen = screens[currentScreenIndex];
    const prevSelectedBtn = selectedElement
      ? currentScreen.elements.find(el => el.id === selectedElement && el.type === 'button')
      : null;
    const prevButtonType = prevSelectedBtn?.buttonType;

    const updatedElements = currentScreen.elements.filter(el => el.type !== 'button');

    if (buttonConfig.back_button) {
      updatedElements.push({
        id: `button_back_${Date.now()}`,
        type: 'button',
        buttonType: 'back',
        position: { x: buttonConfig.back_button.x, y: buttonConfig.back_button.y },
        size: { width: buttonConfig.back_button.width, height: buttonConfig.back_button.height },
        style: buttonConfig.back_button.style || {},
        content: buttonConfig.back_button.content || 'Back'
      });
    }

    if (buttonConfig.next_button) {
      updatedElements.push({
        id: `button_next_${Date.now()}`,
        type: 'button',
        buttonType: 'next',
        position: { x: buttonConfig.next_button.x, y: buttonConfig.next_button.y },
        size: { width: buttonConfig.next_button.width, height: buttonConfig.next_button.height },
        style: buttonConfig.next_button.style || {},
        content: buttonConfig.next_button.content || 'Next'
      });
    }

    const updatedScreen = { ...currentScreen, elements: updatedElements };
    const updatedScreens = screens.map(s => s.id === currentScreen.id ? updatedScreen : s);
    setScreens(updatedScreens);

    // Re-select a button so the Screen Button Layout panel stays open. Prefer a match on
    // buttonType (back→back, next→next); otherwise fall back to whichever button exists.
    const newButtons = updatedElements.filter(el => el.type === 'button');
    const matchByType = prevButtonType
      ? newButtons.find(el => el.buttonType === prevButtonType)
      : null;
    const reselect = matchByType || newButtons[0];
    if (reselect) setSelectedElement(reselect.id);
  };

  const handleSaveQuestionLayoutTemplate = async () => {
    if (!questionTemplateName.trim() || !selectedElement) {
      toast.error('Please enter a template name');
      return;
    }

    const selectedElementData = screens[currentScreenIndex]?.elements?.find(
      el => el.id === selectedElement
    );

    if (!selectedElementData || selectedElementData.type !== 'question') {
      toast.error('Please select a question element');
      return;
    }

    const questionType = selectedElementData.itemData?.question_type || 
                        selectedElementData.itemData?.type || 
                        selectedElementData.data?.question_type || 
                        'default';

    try {
      const templateData = {
        name: questionTemplateName.trim(),
        question_type: questionType,
        layout_config: {
          position: selectedElementData.position,
          size: selectedElementData.size,
          style: selectedElementData.style,
          responseConfig: selectedElementData.responseConfig,
          responseOptions: selectedElementData.responseOptions,
          is_required: selectedElementData.required || false,
        },
        level: 'assessment',
        assessment_id: assessmentConfig.assessment_uuid
      };

      const response = await AssessmentBuilderAPI.saveQuestionLayoutTemplate(templateData);
      
      toast.success(`Saved template "${questionTemplateName}"`);
      setQuestionTemplateName('');
      setQuestionTemplateAsDefault(false);

      // If set as default, make another API call
      if (questionTemplateAsDefault && response?.id) {
        try {
          await AssessmentBuilderAPI.setLayoutDefault({
            template_id: response.id,
            assessment_id: assessmentConfig.assessment_uuid,
            set_as_default: true
          });
          toast.success(`Set as default for ${questionType} questions`);
        } catch (error) {
          console.error('Error setting as default:', error);
          toast.warning('Template saved but could not set as default');
        }
      }
    } catch (error) {
      console.error('Error saving question layout template:', error);
      toast.error('Failed to save template: ' + (error.message || 'Unknown error'));
    }
  };

  const applyButtonTemplateToCurrentScreen = () => {
    const template = buttonTemplates.find(t => t.id == selectedButtonTemplate);
    if (!template) return;

    const currentScreen = screens[currentScreenIndex];
    // Remember which button type was selected so we can re-select it after replacement
    const prevSelectedBtn = selectedElement
      ? currentScreen.elements.find(el => el.id === selectedElement)
      : null;
    const prevButtonType = prevSelectedBtn?.buttonType;

    const nonButtonElements = currentScreen.elements.filter(el => el.type !== 'button');
    const newElements = [...nonButtonElements];

    if (template.button_config.back_button) {
      newElements.push({
        id: `button_back_${currentScreen.id}_${Date.now()}`,
        type: 'button',
        buttonType: 'back',
        position: { x: template.button_config.back_button.x, y: template.button_config.back_button.y },
        size: { width: template.button_config.back_button.width, height: template.button_config.back_button.height },
        style: template.button_config.back_button.style || {},
        content: template.button_config.back_button.content || 'Back'
      });
    }

    if (template.button_config.next_button) {
      newElements.push({
        id: `button_next_${currentScreen.id}_${Date.now()}`,
        type: 'button',
        buttonType: 'next',
        position: { x: template.button_config.next_button.x, y: template.button_config.next_button.y },
        size: { width: template.button_config.next_button.width, height: template.button_config.next_button.height },
        style: template.button_config.next_button.style || {},
        content: template.button_config.next_button.content || 'Next'
      });
    }

    const updatedScreen = { ...currentScreen, elements: newElements };
    const updatedScreens = screens.map(s => s.id === currentScreen.id ? updatedScreen : s);
    setScreens(updatedScreens);

    // Re-select the button that matches the previously selected button type
    if (prevButtonType) {
      const newBtn = newElements.find(el => el.type === 'button' && el.buttonType === prevButtonType);
      if (newBtn) setSelectedElement(newBtn.id);
    }

    toast.success(`Applied "${template.name}" to ${currentScreen.name}`);
  };

  const applyButtonTemplateToAllScreens = () => {
    const template = buttonTemplates.find(t => t.id == selectedButtonTemplate);
    if (!template) return;

    // Remember which button type was selected so we can re-select it after replacement
    const currentScreen = screens[currentScreenIndex];
    const prevSelectedBtn = selectedElement
      ? currentScreen?.elements?.find(el => el.id === selectedElement)
      : null;
    const prevButtonType = prevSelectedBtn?.buttonType;

    setConfirmDialog({
      isOpen: true,
      title: 'Apply to All Screens?',
      message: `This will replace all navigation buttons on every screen with the "${template.name}" layout. This cannot be undone.`,
      type: 'warning',
      confirmText: 'Apply to All Screens',
      onConfirm: () => {
        const updatedScreens = screens.map(screen => {
          const nonButtonElements = screen.elements.filter(el => el.type !== 'button');
          const newElements = [...nonButtonElements];

          if (template.button_config.back_button) {
            newElements.push({
              id: `button_back_${screen.id}_${Date.now()}`,
              type: 'button',
              buttonType: 'back',
              position: { x: template.button_config.back_button.x, y: template.button_config.back_button.y },
              size: { width: template.button_config.back_button.width, height: template.button_config.back_button.height },
              style: template.button_config.back_button.style || {},
              content: template.button_config.back_button.content || 'Back'
            });
          }

          if (template.button_config.next_button) {
            newElements.push({
              id: `button_next_${screen.id}_${Date.now()}`,
              type: 'button',
              buttonType: 'next',
              position: { x: template.button_config.next_button.x, y: template.button_config.next_button.y },
              size: { width: template.button_config.next_button.width, height: template.button_config.next_button.height },
              style: template.button_config.next_button.style || {},
              content: template.button_config.next_button.content || 'Next'
            });
          }

          return { ...screen, elements: newElements };
        });

        setScreens(updatedScreens);

        // Re-select the button that matches the previously selected button type on the current screen
        if (prevButtonType) {
          const updatedCurrent = updatedScreens[currentScreenIndex];
          const newBtn = updatedCurrent?.elements.find(el => el.type === 'button' && el.buttonType === prevButtonType);
          if (newBtn) setSelectedElement(newBtn.id);
        }

        toast.success(`Applied "${template.name}" to all ${screens.length} screens`);
        setConfirmDialog({ isOpen: false });

        // Persist this as the assessment-level default for this device mode
        persistButtonTemplateToAssessment(template.button_config);
      }
    });
  };

  // Fetch button templates on mount and when device mode changes
  useEffect(() => {
    if (assessmentConfig?.assessment_id) {
      fetchButtonTemplates();
    }
  }, [assessmentConfig?.assessment_id, deviceMode]);

  // Auto-apply AssessmentTemplate.button_templates[deviceMode] to screens with no buttons
  useEffect(() => {
    const storedButtonConfig = localAssessmentConfig?.default_templates?.config?.button_templates?.[deviceMode];
    if (!storedButtonConfig || !screens.length) return;

    const screensNeedingButtons = screens.filter(
      screen => !screen.elements?.some(el => el.type === 'button')
    );
    if (screensNeedingButtons.length === 0) return;

    runWithoutUndo(() => {
      setScreens((prev) =>
        prev.map((screen) => {
          const hasButtons = screen.elements?.some((el) => el.type === 'button');
          if (hasButtons) return screen;

          const nonButtonEls = screen.elements?.filter((el) => el.type !== 'button') || [];
          const newEls = [...nonButtonEls];
          if (storedButtonConfig.back_button) {
            newEls.push({
              id: `button_back_${screen.id}_${Date.now()}`,
              type: 'button',
              buttonType: 'back',
              position: { x: storedButtonConfig.back_button.x, y: storedButtonConfig.back_button.y },
              size: {
                width: storedButtonConfig.back_button.width,
                height: storedButtonConfig.back_button.height,
              },
              style: storedButtonConfig.back_button.style || {},
              content: storedButtonConfig.back_button.content || 'Back',
            });
          }
          if (storedButtonConfig.next_button) {
            newEls.push({
              id: `button_next_${screen.id}_${Date.now()}`,
              type: 'button',
              buttonType: 'next',
              position: { x: storedButtonConfig.next_button.x, y: storedButtonConfig.next_button.y },
              size: {
                width: storedButtonConfig.next_button.width,
                height: storedButtonConfig.next_button.height,
              },
              style: storedButtonConfig.next_button.style || {},
              content: storedButtonConfig.next_button.content || 'Next',
            });
          }
          return { ...screen, elements: newEls };
        })
      );
    });
  // Only run once when localAssessmentConfig and deviceMode are first available
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAssessmentConfig?.default_templates?.config, deviceMode, runWithoutUndo, setScreens]);

  // Cleanup duplicate screens (utility function)
  const removeDuplicateScreens = () => {
    const seenNames = new Set();
    const uniqueScreens = [];
    
    screens.forEach((screen, index) => {
      const key = `${screen.name}-${screen.rank || index}`;
      if (!seenNames.has(key)) {
        seenNames.add(key);
        uniqueScreens.push(screen);
      } else {
        console.log(`Removing duplicate screen: ${screen.name} (ID: ${screen.id})`);
      }
    });
    
    if (uniqueScreens.length < screens.length) {
      setConfirmDialog({
        isOpen: true,
        title: 'Remove Duplicate Screens?',
        message: `Found ${screens.length - uniqueScreens.length} duplicate screens. Remove them?`,
        type: 'warning',
        confirmText: 'Remove Duplicates',
        onConfirm: () => {
          setScreens(uniqueScreens);
          if (currentScreenIndex >= uniqueScreens.length) {
            setCurrentScreenIndex(Math.max(0, uniqueScreens.length - 1));
          }
          toast.success(`Removed ${screens.length - uniqueScreens.length} duplicate screens`);
          setConfirmDialog({ isOpen: false });
        }
      });
    } else {
      toast('No duplicate screens found', { icon: 'ℹ️' });
    }
  };

  // Cleanup animation frames on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (rafIdResizeRef.current) {
        cancelAnimationFrame(rafIdResizeRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
          <div className="absolute inset-0 rounded-full border-4 border-primary-600 border-t-transparent animate-spin"></div>
        </div>
        <p className="mt-4 text-gray-600 font-medium">Loading Screen Builder...</p>
      </div>
    );
  }

  const currentScreen = screens[currentScreenIndex];
  const selectedElementData = currentScreen?.elements?.find(el => el.id === selectedElement);

  return (
    <div
      className="h-screen w-screen flex flex-col bg-gray-900 overflow-hidden fixed inset-0 z-[9998]"
      style={reviewReadOnly && !viewOnly ? { bottom: 72, height: 'auto' } : undefined}
    >
      <style>{`
        /* Ensure Screen Builder isn't constrained by Layout */
        body.screenbuilder-active {
          overflow: hidden !important;
        }
        
        /* Remove ALL constraints from Layout when Screen Builder is active */
        body.screenbuilder-active main,
        body.screenbuilder-active main > div {
          max-width: none !important;
          padding: 0 !important;
          margin: 0 !important;
          width: 100% !important;
          height: 100% !important;
        }
        
        /* Hide dev environment footer in Screen Builder, but keep the env-dev-banner visible */
        body.screenbuilder-active .fixed.bottom-0 {
          display: none !important;
        }
        
        /* Always keep the env dev banner visible in Screen Builder (higher specificity overrides the hide rule) */
        body.screenbuilder-active .fixed.bottom-0.env-dev-banner {
          display: block !important;
        }
        
        /* Custom scrollbar styling for table */
        .overflow-x-auto::-webkit-scrollbar {
          height: 8px;
        }
        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #14b8a6;
          border-radius: 4px;
        }
        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #0d9488;
        }
        
        /* Thin scrollbar for sidebar panel */
        .sidebar-panel ::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar-panel ::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-panel ::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 4px;
        }
        .sidebar-panel ::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        
        /* Custom checkbox styling */
        .custom-checkbox {
          appearance: none;
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          background-color: white;
          cursor: pointer;
          position: relative;
          transition: all 0.15s ease-in-out;
        }
        .custom-checkbox:hover {
          border-color: #14b8a6;
        }
        .custom-checkbox:checked {
          background-color: #14b8a6;
          border-color: #14b8a6;
        }
        .custom-checkbox:checked::after {
          content: '';
          position: absolute;
          left: 4px;
          top: 1px;
          width: 5px;
          height: 9px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        .custom-checkbox:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.2);
        }
        
        /* Indicate scrollable content with fade */
        .overflow-x-auto {
          position: relative;
        }
      `}</style>
      {/* Premium Top Bar - Light Theme */}
      <BuilderToolbar
        reviewReadOnly={editChromeReadOnly}
        pendingReview={reviewReadOnly && !viewOnly}
        onBack={onBack}
        localAssessmentConfig={localAssessmentConfig}
        editingAssessmentName={editingAssessmentName}
        assessmentNameValue={assessmentNameValue}
        setAssessmentNameValue={setAssessmentNameValue}
        saveAssessmentName={saveAssessmentName}
        cancelEditingAssessmentName={cancelEditingAssessmentName}
        startEditingAssessmentName={startEditingAssessmentName}
        saveStatus={saveStatus}
        lastSaved={lastSaved}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        snapToGrid={snapToGrid}
        setSnapToGrid={setSnapToGrid}
        showAlignmentGuides={showAlignmentGuides}
        setShowAlignmentGuides={setShowAlignmentGuides}
        showPositionOverlay={showPositionOverlay}
        setShowPositionOverlay={setShowPositionOverlay}
        showOutOfBoundsWarning={showOutOfBoundsWarning}
        fitElementsToCanvas={fitElementsToCanvas}
        sidebarPosition={sidebarPosition}
        setSidebarPosition={setSidebarPosition}
        saving={saving}
        handleSave={handleSave}
        showSaveDropdown={showSaveDropdown}
        setShowSaveDropdown={setShowSaveDropdown}
        enterPreviewMode={enterPreviewMode}
        currentScreenIndex={currentScreenIndex}
        showPreviewDropdown={showPreviewDropdown}
        setShowPreviewDropdown={setShowPreviewDropdown}
        showVersionsDropdown={showVersionsDropdown}
        setShowVersionsDropdown={setShowVersionsDropdown}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undoHistory}
        onRedo={redoHistory}
        onOpenSettings={openSettingsModal}
      />

      {superuserBypassActive && (
        <div className={`flex items-center justify-between px-4 py-2 text-sm border-b ${
          superuserReadOnly
            ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
            : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {superuserReadOnly ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            <span className="font-medium">
              {superuserReadOnly
                ? 'Superuser bypass — read-only. Saves are disabled.'
                : 'Superuser bypass — UNLOCKED. Saves write to the published assessment.'}
            </span>
          </div>
          <button
            onClick={() => setSuperuserReadOnly(prev => !prev)}
            className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
              superuserReadOnly
                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {superuserReadOnly ? 'Unlock for Editing' : 'Re-lock'}
          </button>
        </div>
      )}

      {/* Exclusive edit lock banner (FECOA-1302): shown to viewers when they are
          not the confirmed lock holder. Fail-safe — a permission denial or a
          server/network error keeps the editor read-only rather than silently
          editable, so no one can edit without holding the lock. */}
      {lockReadOnly && lockReason === 'locked' && lockHolder && (
        <div className="flex items-center gap-2 px-4 py-2 text-sm border-b bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">
            {`${lockHolder.name} is editing this assessment`}
            {lockSince ? ` since ${new Date(lockSince).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
            . You are in read-only mode and changes cannot be saved.
          </span>
          <span className="ml-auto text-xs opacity-75">Editing unlocks automatically when they finish.</span>
        </div>
      )}
      {lockReadOnly && lockReason === 'denied' && (
        <div className="flex items-center gap-2 px-4 py-2 text-sm border-b bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">
            {lockHolder
              ? <>
                  {`${lockHolder.name} is editing this assessment`}
                  {lockSince ? ` since ${new Date(lockSince).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                  . You are in read-only mode and changes cannot be saved.
                </>
              : 'You do not have edit access to this assessment. You are in read-only mode and changes cannot be saved.'}
          </span>
        </div>
      )}
      {lockReadOnly && lockReason === 'error' && (
        <div className="flex items-center gap-2 px-4 py-2 text-sm border-b bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
          <Lock className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">
            Couldn't secure an edit lock for this assessment. You are in read-only mode until the connection is restored.
          </span>
          <span className="ml-auto text-xs opacity-75">Retrying…</span>
        </div>
      )}

      {/* Changes-requested banner (rejection flow): when a submitter opens a
          denied assessment — typically by clicking the rejection notification —
          surface the reviewer's reason here so they can start making the
          required changes. Clears itself once the assessment is resubmitted. */}
      <ChangesRequestedBanner
        assessmentId={localAssessmentConfig?.assessment_id || assessmentConfig?.assessment_id}
        entityName={localAssessmentConfig?.name || assessmentConfig?.name}
        status={localAssessmentConfig?.publication_status || assessmentConfig?.publication_status}
      />

      {/* Main Content Area */}
      <div className={`flex flex-1 overflow-hidden relative ${
        sidebarPosition === 'bottom' ? 'flex-col' : 'flex-row'
      }`}>
        {/* Collapsible Sidebar/Panel - Left or Bottom (hidden in review / when lock-held) */}
        {!editChromeReadOnly && !sidebarCollapsed && sidebarPosition !== 'right' && (
          <SidebarPanel
            position={sidebarPosition}
            sidebarWidth={sidebarWidth}
            activePanel={activePanel}
            setActivePanel={setActivePanel}
            selectedElement={selectedElement}
            selectedElements={selectedElements}
            onDeleteSelected={deleteSelectedElements}
            handleSidebarResizeStart={handleSidebarResizeStart}
            setDraggingFromSidebar={setDraggingFromSidebar}
            setDragPreviewPosition={setDragPreviewPosition}
            items={items}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setShowItemBank={setShowItemBank}
            addElementToScreen={addElementToScreen}
            removeAssessmentItem={removeAssessmentItem}
            removeAllItems={removeAllItems}
            onEditItem={(item) => { setEditItemFromSidebar(item); setShowItemBank(true); }}
            selectedElementData={selectedElementData}
            setScreens={setScreens}
            setConfirmDialog={setConfirmDialog}
            calculateQuestionHeight={calculateQuestionHeight}
            updateElementContent={updateElementContent}
            distributeOptions={distributeOptions}
            alignOptions={alignOptions}
            toggleLockElement={toggleLockElement}
            isElementLocked={isElementLocked}
            deleteElement={deleteElement}
            ungroupQuestion={ungroupQuestion}
            regroupQuestion={regroupQuestion}
            updateSubElementPosition={updateSubElementPosition}
            buttonTemplates={buttonTemplates}
            selectedButtonTemplate={selectedButtonTemplate}
            handleLoadButtonTemplate={handleLoadButtonTemplate}
            handleSaveButtonTemplate={handleSaveButtonTemplate}
            handleDeleteButtonTemplate={handleDeleteButtonTemplate}
            handleApplyTemplateToAllScreens={applyButtonTemplateToAllScreens}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            templateSaveLevel={templateSaveLevel}
            setTemplateSaveLevel={setTemplateSaveLevel}
            setTemplateAsDefault={setTemplateAsDefault}
            setSetTemplateAsDefault={setSetTemplateAsDefault}
            updateElementPosition={updateElementPosition}
            updateElementSize={updateElementSize}
            navigationRules={navigationRules}
            setNavigationRules={setNavigationRules}
            setShowRuleEditorModal={setShowRuleEditorModal}
            setEditingRule={setEditingRule}
            assessmentId={assessmentConfig?.assessment_uuid}
            deviceMode={deviceMode}
            deviceDimensions={filteredDeviceDimensions}
            handleDeviceModeChange={handleDeviceModeChange}
            setSelectedElement={setSelectedElement}
            setCurrentScreenIndex={setCurrentScreenIndex}
            allTemplatesCache={allTemplatesCache}
            refreshTemplatesCache={refreshTemplatesCache}
            appliedDefaultTemplateIds={appliedDefaultTemplateIds}
            onDefaultTemplateSet={(templateObj, questionType) => {
              setLocalAssessmentConfig(prev => ({
                ...prev,
                default_templates: {
                  ...(prev.default_templates || {}),
                  config: {
                    ...(prev.default_templates?.config || {}),
                    question_templates: {
                      ...(prev.default_templates?.config?.question_templates || {}),
                      [questionType]: {
                        template_id: templateObj.id,
                        template_name: templateObj.name,
                        question_type: questionType,
                        layout_config: templateObj.layout_config
                      }
                    }
                  }
                }
              }));
            }}
            globalFormatting={globalFormatting}
            onGlobalFormattingChange={handleGlobalFormattingChange}
          />
        )}

        {/* Sidebar Collapse Button - Hugs the sidebar edge, vertically centered */}
        {!editChromeReadOnly && (
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={`absolute z-20 bg-gray-800/90 text-gray-300 p-1.5 hover:bg-gray-700 transition-colors shadow-md ${
            sidebarPosition === 'left'
              ? sidebarCollapsed
                ? 'left-0 top-1/2 -translate-y-1/2 rounded-r-md'
                : `top-1/2 -translate-y-1/2 rounded-r-md`
              : sidebarPosition === 'right'
              ? sidebarCollapsed
                ? 'right-0 top-1/2 -translate-y-1/2 rounded-l-md'
                : `top-1/2 -translate-y-1/2 rounded-l-md`
              : sidebarCollapsed
                ? 'left-1/2 -translate-x-1/2 bottom-0 rounded-t-md'
                : 'left-1/2 -translate-x-1/2 rounded-t-md'
          }`}
          style={
            !sidebarCollapsed && sidebarPosition === 'left' ? { left: `${sidebarWidth}px` } :
            !sidebarCollapsed && sidebarPosition === 'right' ? { right: `${sidebarWidth}px` } :
            !sidebarCollapsed && sidebarPosition === 'bottom' ? { bottom: '256px' } :
            {}
          }
          title={sidebarCollapsed ? "Show Panel" : "Hide Panel"}
        >
          {sidebarCollapsed ? (
            sidebarPosition === 'left' ? <PanelLeftOpen className="w-3.5 h-3.5" /> :
            sidebarPosition === 'right' ? <ChevronLeft className="w-3.5 h-3.5" /> :
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            sidebarPosition === 'left' ? <PanelLeftClose className="w-3.5 h-3.5" /> :
            sidebarPosition === 'right' ? <ChevronRight className="w-3.5 h-3.5" /> :
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>
        )}

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <ScreenTabs
            reviewReadOnly={editChromeReadOnly}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setCurrentScreenIndex={setCurrentScreenIndex}
            editingScreenId={editingScreenId}
            editingScreenName={editingScreenName}
            setEditingScreenName={setEditingScreenName}
            saveScreenName={saveScreenName}
            cancelEditingScreenName={cancelEditingScreenName}
            startEditingScreenName={startEditingScreenName}
            duplicateScreen={duplicateScreen}
            deleteScreen={deleteScreen}
            deleteAllScreens={deleteAllScreens}
            addNewScreen={addNewScreen}
            setShowBulkAddModal={setShowBulkAddModal}
            draggingScreenIndex={draggingScreenIndex}
            dragOverScreenIndex={dragOverScreenIndex}
            setDragOverScreenIndex={setDragOverScreenIndex}
            handleScreenDragStart={handleScreenDragStart}
            handleScreenDragOver={handleScreenDragOver}
            handleScreenDrop={handleScreenDrop}
            handleScreenDragEnd={handleScreenDragEnd}
            fitElementsToCanvas={fitElementsToCanvas}
          />

          {/* Canvas */}
          <CanvasArea
            reviewReadOnly={editChromeReadOnly}
            screens={screens}
            currentScreen={currentScreen}
            currentScreenIndex={currentScreenIndex}
            canvasZoom={canvasZoom}
            canvasPan={canvasPan}
            isPanning={isPanning}
            showGrid={showGrid}
            gridSize={gridSize}
            showAlignmentGuides={showAlignmentGuides}
            showPositionOverlay={showPositionOverlay}
            showProperties={showProperties}
            setShowProperties={setShowProperties}
            showOutOfBoundsWarning={showOutOfBoundsWarning}
            setShowOutOfBoundsWarning={setShowOutOfBoundsWarning}
            selectedElement={selectedElement}
            setSelectedElement={setSelectedElement}
            selectedElements={selectedElements}
            editingElement={editingElement}
            setEditingElement={setEditingElement}
            editingValue={editingValue}
            setEditingValue={setEditingValue}
            dragging={dragging}
            resizing={resizing}
            draggingFromSidebar={draggingFromSidebar}
            setDraggingFromSidebar={setDraggingFromSidebar}
            dragPreviewPosition={dragPreviewPosition}
            setDragPreviewPosition={setDragPreviewPosition}
            draggingScreenIndex={draggingScreenIndex}
            dragOverScreenIndex={dragOverScreenIndex}
            setDragOverScreenIndex={setDragOverScreenIndex}
            isElementLocked={isElementLocked}
            handleCanvasPanStart={handleCanvasPanStart}
            handleMouseDown={handleMouseDown}
            handleDoubleClick={handleDoubleClick}
            handleInlineEditSave={handleInlineEditSave}
            handleInlineEditCancel={handleInlineEditCancel}
            handleResizeStart={handleResizeStart}
            handleScreenDrop={handleScreenDrop}
            updateSubElementPosition={updateSubElementPosition}
            updateSubElementSize={updateSubElementSize}
            updateResponseConfig={updateResponseConfig}
            snapPosition={snapPosition}
            addNewScreen={addNewScreen}
            addElementToScreen={addElementToScreen}
            fitElementsToCanvas={fitElementsToCanvas}
            getOutOfBoundsElements={getOutOfBoundsElements}
          />
        </div>

        {/* Right Sidebar (hidden in review / when lock-held) */}
        {!editChromeReadOnly && !sidebarCollapsed && sidebarPosition === 'right' && (
          <SidebarPanel
            position="right"
            sidebarWidth={sidebarWidth}
            activePanel={activePanel}
            setActivePanel={setActivePanel}
            selectedElement={selectedElement}
            selectedElements={selectedElements}
            onDeleteSelected={deleteSelectedElements}
            handleSidebarResizeStart={handleSidebarResizeStart}
            setDraggingFromSidebar={setDraggingFromSidebar}
            setDragPreviewPosition={setDragPreviewPosition}
            items={items}
            screens={screens}
            currentScreenIndex={currentScreenIndex}
            setShowItemBank={setShowItemBank}
            addElementToScreen={addElementToScreen}
            removeAssessmentItem={removeAssessmentItem}
            removeAllItems={removeAllItems}
            onEditItem={(item) => { setEditItemFromSidebar(item); setShowItemBank(true); }}
            selectedElementData={selectedElementData}
            setScreens={setScreens}
            setConfirmDialog={setConfirmDialog}
            calculateQuestionHeight={calculateQuestionHeight}
            updateElementContent={updateElementContent}
            distributeOptions={distributeOptions}
            alignOptions={alignOptions}
            toggleLockElement={toggleLockElement}
            isElementLocked={isElementLocked}
            deleteElement={deleteElement}
            ungroupQuestion={ungroupQuestion}
            regroupQuestion={regroupQuestion}
            updateSubElementPosition={updateSubElementPosition}
            buttonTemplates={buttonTemplates}
            selectedButtonTemplate={selectedButtonTemplate}
            handleLoadButtonTemplate={handleLoadButtonTemplate}
            handleSaveButtonTemplate={handleSaveButtonTemplate}
            handleDeleteButtonTemplate={handleDeleteButtonTemplate}
            handleApplyTemplateToAllScreens={applyButtonTemplateToAllScreens}
            newTemplateName={newTemplateName}
            setNewTemplateName={setNewTemplateName}
            templateSaveLevel={templateSaveLevel}
            setTemplateSaveLevel={setTemplateSaveLevel}
            setTemplateAsDefault={setTemplateAsDefault}
            setSetTemplateAsDefault={setSetTemplateAsDefault}
            updateElementPosition={updateElementPosition}
            updateElementSize={updateElementSize}
            navigationRules={navigationRules}
            setNavigationRules={setNavigationRules}
            setShowRuleEditorModal={setShowRuleEditorModal}
            setEditingRule={setEditingRule}
            assessmentId={assessmentConfig?.assessment_uuid}
            deviceMode={deviceMode}
            deviceDimensions={filteredDeviceDimensions}
            handleDeviceModeChange={handleDeviceModeChange}
            setSelectedElement={setSelectedElement}
            setCurrentScreenIndex={setCurrentScreenIndex}
            allTemplatesCache={allTemplatesCache}
            refreshTemplatesCache={refreshTemplatesCache}
            appliedDefaultTemplateIds={appliedDefaultTemplateIds}
            onDefaultTemplateSet={(templateObj, questionType) => {
              setLocalAssessmentConfig(prev => ({
                ...prev,
                default_templates: {
                  ...(prev.default_templates || {}),
                  config: {
                    ...(prev.default_templates?.config || {}),
                    question_templates: {
                      ...(prev.default_templates?.config?.question_templates || {}),
                      [questionType]: {
                        template_id: templateObj.id,
                        template_name: templateObj.name,
                        question_type: questionType,
                        layout_config: templateObj.layout_config
                      }
                    }
                  }
                }
              }));
            }}
            globalFormatting={globalFormatting}
            onGlobalFormattingChange={handleGlobalFormattingChange}
          />
        )}

        {/* Version History Right Sidebar */}
        <VersionHistorySidebar
          showVersionsDropdown={showVersionsDropdown}
          setShowVersionsDropdown={setShowVersionsDropdown}
          versionHistoryRef={versionHistoryRef}
          surveyId={assessmentConfig?.survey_id}
          setScreens={setScreens}
          setDeviceMode={setDeviceMode}
          setCurrentScreenIndex={setCurrentScreenIndex}
          setSelectedElement={setSelectedElement}
          clearHistory={clearHistory}
          runWithoutUndo={runWithoutUndo}
          saveRestoredVersion={saveRestoredVersion}
        />

        <ZoomControls
          canvasZoom={canvasZoom}
          setCanvasZoom={setCanvasZoom}
          showVersionsDropdown={showVersionsDropdown}
          sidebarPosition={sidebarPosition}
          sidebarCollapsed={sidebarCollapsed}
          sidebarWidth={sidebarWidth}
        />
      </div>

      {/* Device Mode Change Confirmation Modal */}
      <DeviceModeModal
        showDeviceModeModal={showDeviceModeModal}
        pendingDeviceMode={pendingDeviceMode}
        deviceMode={deviceMode}
        deviceDimensions={filteredDeviceDimensions}
        selectedScalingOption={selectedScalingOption}
        setSelectedScalingOption={setSelectedScalingOption}
        setShowDeviceModeModal={setShowDeviceModeModal}
        setPendingDeviceMode={setPendingDeviceMode}
        applyDeviceModeChange={applyDeviceModeChange}
      />


      {/* Locked Assessment Warning Modal */}
      <PublishedWarningModal
        showPublishedWarningModal={showPublishedWarningModal}
        setShowPublishedWarningModal={setShowPublishedWarningModal}
        creatingNewVersion={creatingNewVersion}
        handleCreateNewVersion={handleCreateNewVersion}
        onBack={onBack}
        isSuperuser={user?.is_superuser}
        onOpenReadOnly={enterSuperuserReadOnlyBypass}
        publicationStatus={assessmentConfig?.publication_status}
      />

      {/* Publish Modal */}
      <PublishModal
        showPublishModal={showPublishModal}
        publishName={publishName}
        setPublishName={setPublishName}
        localAssessmentConfig={localAssessmentConfig}
        changeSummary={changeSummary}
        setChangeSummary={setChangeSummary}
        setShowPublishModal={setShowPublishModal}
        handlePublish={handlePublish}
        publishing={publishing}
      />


      {/* Rule Editor Modal */}
      <RuleEditorModal
        showRuleEditorModal={showRuleEditorModal}
        editingRule={editingRule}
        setEditingRule={setEditingRule}
        setShowRuleEditorModal={setShowRuleEditorModal}
        ruleFormData={ruleFormData}
        setRuleFormData={setRuleFormData}
        items={items}
        screens={screens}
        currentScreenIndex={currentScreenIndex}
        selectedElement={selectedElement}
        navigationRules={navigationRules}
        setNavigationRules={setNavigationRules}
        setConfirmDialog={setConfirmDialog}
        saveNavigationRulesToBackend={saveNavigationRulesToBackend}
      />
      {/* Bulk Add Screens Modal */}
      <BulkAddModal
        showBulkAddModal={showBulkAddModal}
        setShowBulkAddModal={setShowBulkAddModal}
        bulkScreenCount={bulkScreenCount}
        setBulkScreenCount={setBulkScreenCount}
        screensLength={screens.length}
        addBulkScreens={addBulkScreens}
      />


      {/* TEMPORARILY DISABLED: Skip Logic Templates
       * The template feature allows users to save and reuse skip logic patterns.
       * However, skip logic is highly assessment-specific (screen IDs, question IDs, thresholds)
       * making reusable templates less practical than originally planned.
       * 
       * To re-enable this feature:
       * 1. Uncomment showTemplatePickerModal state declaration (~line 121)
       * 2. Uncomment navigationTemplates, selectedTemplate, loadingTemplates states (~line 135-137)
       * 3. Uncomment loadNavigationTemplates and applyNavigationTemplateToQuestion functions (~line 669-715)
       * 4. Uncomment the useEffect that loads templates when modal opens (~line 2629-2647)
       * 5. Uncomment the "Use Template" button in Navigation tab (~line 6039-6047)
       * 6. Uncomment this Template Picker Modal below
       * 7. Add backend CRUD endpoints for creating/updating/deleting sponsor templates
       *    (currently only GET and APPLY endpoints exist)
       */}

      {/* First Question Modal */}
      <FirstQuestionModal
        show={showFirstQuestionModal}
        onClose={() => {
          // User clicked Skip - still add the element, just without template
          if (pendingElement) {
            addElementToScreen(
              pendingElement.itemType,
              pendingElement.item,
              pendingElement.customPosition,
              true // skipFirstQuestionCheck
            );
          }
          setShowFirstQuestionModal(false);
          setPendingElement(null);
          setFirstQuestionType(null);
        }}
        questionType={firstQuestionType}
        assessmentId={assessmentConfig?.assessment_uuid}
        questionData={pendingElement?.item}
        onTemplateSelected={handleFirstQuestionTemplateSelected}
        onPreferenceChange={(enabled) => setAutoShowLayoutModal(enabled)}
      />

      {/* Alert Modal */}
      <AlertModal alertModal={alertModal} setAlertModal={setAlertModal} />

      {/* Preview Mode */}
      <PreviewMode
        previewMode={previewMode}
        screens={screens}
        previewScreenIndex={previewScreenIndex}
        previewResponses={previewResponses}
        setPreviewResponses={setPreviewResponses}
        previewShowDebug={previewShowDebug}
        setPreviewShowDebug={setPreviewShowDebug}
        previewCompleted={previewCompleted}
        previewStartTime={previewStartTime}
        previewNavigationLog={previewNavigationLog}
        setPreviewNavigationLog={setPreviewNavigationLog}
        previewScreenTimes={previewScreenTimes}
        previewSkipLogMessage={previewSkipLogMessage}
        previewValidationErrors={previewValidationErrors}
        navigationRules={navigationRules}
        previewNavigationHistory={previewNavigationHistory}
        previewScreenVisits={previewScreenVisits}
        previewFiredRuleIds={previewFiredRuleIds}
        setPreviewFiredRuleIds={setPreviewFiredRuleIds}
        setPreviewSkippedQuestions={setPreviewSkippedQuestions}
        allowBackNavigation={localAssessmentConfig?.builder_metadata?.allow_back_navigation ?? true}
        exitPreviewMode={exitPreviewMode}
        enterPreviewMode={enterPreviewMode}
        navigatePreviewScreen={navigatePreviewScreen}
        navigatePreviewBack={navigatePreviewBack}
        validateCurrentScreen={validateCurrentScreen}
        completePreviewAssessment={completePreviewAssessment}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText || 'Delete'}
        cancelText="Cancel"
      />

      {/* Unified Item Bank Modal */}
      <UnifiedItemBank
        isOpen={showItemBank}
        onClose={() => { setShowItemBank(false); setEditItemFromSidebar(null); }}
        initialEditItem={editItemFromSidebar}
        onInitialEditHandled={() => setEditItemFromSidebar(null)}
        deviceMode={deviceMode}
        onItemUpdated={(sidebarItemId, updatedItem) => {
          // Update the sidebar items list with the edited item data
          setItems(prevItems => prevItems.map(item => {
            if (item.id === sidebarItemId || item.item_bank_id === updatedItem.id || (updatedItem.code && item.code === updatedItem.code)) {
              return {
                ...item,
                text: updatedItem.text || item.text,
                code: updatedItem.code || item.code,
                type: updatedItem.responseType || item.type,
                response_type: updatedItem.responseType || item.response_type,
                question_type: updatedItem.responseType || item.question_type,
                options: updatedItem.responseOptions || item.options,
                response_options: updatedItem.responseOptions || item.response_options,
                domain: updatedItem.domain || item.domain,
                category: updatedItem.category || item.category,
                source: updatedItem.source || item.source,
                validated: updatedItem.validated ?? item.validated,
              };
            }
            return item;
          }));
          // Also update canvas elements that reference this item.
          // FECOA-1437: when the response type changes (e.g. Free Text → Yes/No),
          // rebuild responseConfig + responseOptions for the new type so the
          // canvas widget actually swaps, not just the underlying data.type.
          // FECOA-1481: instruction items are placed on the canvas as
          // type='instruction' (not 'question') — they also need their text
          // refreshed when edited.
          const elementMatchesItem = (el) => {
            const ids = [
              el.questionId,
              el.data?.id,
              el.itemData?.id,
              el.data?.item_bank_id,
              el.itemData?.item_bank_id,
            ].filter(v => v !== undefined && v !== null);
            if (ids.includes(sidebarItemId)) return true;
            if (ids.includes(updatedItem.id)) return true;
            if (updatedItem.code && (el.data?.code === updatedItem.code || el.itemData?.code === updatedItem.code)) return true;
            return false;
          };
          setScreens(prevScreens => prevScreens.map(screen => ({
            ...screen,
            elements: screen.elements.map(el => {
              if (el.type !== 'question' && el.type !== 'instruction') return el;
              if (!elementMatchesItem(el)) return el;
              if (el.type === 'instruction') {
                return {
                  ...el,
                  content: updatedItem.text ?? el.content,
                  data: { ...el.data, text: updatedItem.text, code: updatedItem.code ?? el.data?.code },
                  itemData: { ...el.itemData, text: updatedItem.text, code: updatedItem.code ?? el.itemData?.code },
                };
              }
              const typeChanged = el.data?.type !== updatedItem.responseType;
              if (typeChanged) {
                const rebuilt = buildResponseConfigForType(updatedItem.responseType, updatedItem.responseOptions);
                return {
                  ...el,
                  content: updatedItem.text || el.content,
                  data: {
                    ...el.data,
                    text: updatedItem.text,
                    type: updatedItem.responseType,
                    code: updatedItem.code,
                    options: rebuilt.responseOptions.length > 0 ? rebuilt.responseOptions : updatedItem.responseOptions,
                  },
                  itemData: {
                    ...el.itemData,
                    text: updatedItem.text,
                    type: updatedItem.responseType,
                    code: updatedItem.code,
                    options: updatedItem.responseOptions,
                  },
                  responseConfig: rebuilt.responseConfig,
                  responseOptions: rebuilt.responseOptions,
                };
              }
              const newOptions = buildCanvasOptionsFromUpdatedItem(updatedItem.responseOptions);
              return {
                ...el,
                content: updatedItem.text || el.content,
                data: { ...el.data, text: updatedItem.text, type: updatedItem.responseType, code: updatedItem.code, options: newOptions },
                itemData: { ...el.itemData, text: updatedItem.text, type: updatedItem.responseType, code: updatedItem.code, options: updatedItem.responseOptions },
                responseOptions: newOptions,
              };
            })
          })));
          toast.success(`Item "${updatedItem.code || 'item'}" updated`);
        }}
        onItemDeleted={(deletedItemId, deletedItem, deletedCopiesCount) => {
          // Remove deleted item from sidebar items list
          // Match by ID, code, content_hash, or item_bank_id since assessment copies may have different IDs
          setItems(prevItems => prevItems.filter(item => {
            // Direct ID match
            if (item.id === deletedItemId) return false;
            // Item bank ID match (tracks source item)
            if (item.item_bank_id === deletedItemId) return false;
            // Code match (assessment copies share same code)
            if (deletedItem?.code && item.code === deletedItem.code) return false;
            // Content hash match
            if (deletedItem?.content_hash && item.content_hash === deletedItem.content_hash) return false;
            return true;
          }));
          
          if (deletedCopiesCount > 0) {
            toast.success(`Removed item and ${deletedCopiesCount} assessment copies`);
          }
        }}
        onAddItem={(itemDataOrArray) => {
          // Handle both single item and array of items (batch import)
          const itemsToAdd = Array.isArray(itemDataOrArray) ? itemDataOrArray : [itemDataOrArray];
          const isBatchImport = Array.isArray(itemDataOrArray);
          
          let addedCount = 0;
          let skippedCount = 0;
          
          setItems(prevItems => {
            const newItemsList = [...prevItems];
            
            for (const itemData of itemsToAdd) {
              if (!itemData?.itemData) continue;
              
              const itemId = itemData.itemData.id;
              
              // Check for duplicate - prevent adding same item twice
              const isDuplicate = newItemsList.some(existing => 
                existing.id === itemId || 
                (existing.code === itemData.itemData.code && existing.text === itemData.itemData.text)
              );
              
              if (isDuplicate) {
                skippedCount++;
                // Only show error for single item adds, not batch imports
                if (!isBatchImport) {
                  toast.error(`Item "${itemData.itemData.code || 'item'}" is already in this assessment`);
                }
                continue;
              }
              
              const newItem = {
                id: itemId || `imported_${Date.now()}_${addedCount}`,
                text: itemData.itemData.text,
                type: itemData.itemData.response_type || itemData.itemData.responseType || itemData.itemData.type || 'ordinal',
                question_type: itemData.itemData.response_type || itemData.itemData.responseType || itemData.itemData.type || 'ordinal',
                response_type: itemData.itemData.response_type || itemData.itemData.responseType || itemData.itemData.type || 'ordinal',
                options: itemData.responseOptions || itemData.itemData.response_options || itemData.itemData.responseOptions || itemData.itemData.options || [],
                response_options: itemData.responseOptions || itemData.itemData.response_options || itemData.itemData.responseOptions || itemData.itemData.options || [],
                rank: newItemsList.length,
                code: itemData.itemData.code,
                domain: itemData.itemData.domain,
                source: itemData.itemData.source,
                validated: itemData.itemData.validated,
                irt_parameters: itemData.itemData.irt_parameters || {},
                content_hash: itemData.itemData.content_hash,
                item_bank_id: itemData.itemData.id,
              };
              newItemsList.push(newItem);
              addedCount++;
            }
            
            return newItemsList;
          });
          
          // Show appropriate toast message
          if (isBatchImport) {
            if (addedCount > 0 && skippedCount > 0) {
              toast.success(`Added ${addedCount} items (${skippedCount} already existed)`);
            } else if (addedCount > 0) {
              toast.success(`Added ${addedCount} items to assessment`);
            } else if (skippedCount > 0) {
              toast.info(`All ${skippedCount} items already in assessment`);
            }
          } else if (addedCount > 0) {
            toast.success(`Added "${itemsToAdd[0]?.itemData?.code || 'item'}" to assessment`);
          }
          // Don't auto-close - let user add multiple items and close when done
        }}
        onScreensBuilt={() => setRefreshCounter(c => c + 1)}
        libraryType={editItemFromSidebar?.source_library || 'assessment'}
        currentAssessment={localAssessmentConfig}
        assessmentItems={items}
      />

      {/* Manage Assessment modal opened from the toolbar gear icon */}
      {manageModalAssessment && (
        <ManageAssessment
          assessment={manageModalAssessment}
          defaultTab="settings"
          onClose={() => setManageModalAssessment(null)}
          onUpdate={(updated) => setManageModalAssessment(updated)}
        />
      )}

    </div>
  );
};

export default ScreenBuilder;
